import React, { useState } from 'react';
import { FaChevronLeft, FaChevronRight, FaBars, FaCalendarAlt, FaClock, FaBook, FaDollarSign, FaLaptop, FaSchool, FaMapMarkerAlt, FaUsers, FaGraduationCap, FaPaperclip, FaFile, FaTicketAlt, FaCheckCircle, FaCreditCard, FaHourglassHalf, FaTimesCircle, FaExclamationTriangle, FaGift, FaLock, FaVideo, FaRobot, FaChartBar, FaClipboardList, FaCheck, FaLink, FaEnvelope, FaRocket } from 'react-icons/fa';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isToday, eachDayOfInterval, compareAsc } from 'date-fns';
import { useLocation, useOutletContext } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { createBookingRequest, getStudentBookingRequests, getTeacherBookingRequests, deleteBookingRequest, requestReschedule } from './bookingService';
import { getAvailableCoupons } from './walletService';
import TimeSelect from './components/TimeSelect';
import { Calendar as ShadcnCalendar } from './components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { Button } from './components/ui/button';
import PageHeader from './components/PageHeader';
import LocationPicker from './components/location/LocationPicker';
import { createMapsSearchUrl, createMapsUrl } from './components/location/locationUtils';
import logger from './utils/logger';


const mockEvents = {
  // Add some events for the current month to show in the agenda
  [format(new Date(), 'yyyy-MM-dd')]: [{ title: 'Today\'s Meeting', color: '#1E293B' }],
  [format(addDays(new Date(), 2), 'yyyy-MM-dd')]: [{ title: 'Project Deadline', color: '#ff7426' }],
  [format(addDays(new Date(), 5), 'yyyy-MM-dd')]: [{ title: 'Class with Ahmed', color: '#1E293B' }, { title: 'Submit Homework', color: '#ffb400' }],
  [format(addDays(new Date(), 10), 'yyyy-MM-dd')]: [{ title: 'Review Session', color: '#ff7426' }],
};

// Helper to generate time options in 30-min intervals
const generateTimeOptions = () => {
  const times = [];
  for (let h = 1; h <= 12; h++) {
    for (let m of [0, 30]) {
      for (let period of ['AM', 'PM']) {
        times.push({
          hour: h,
          minute: m,
          period,
          label: `${h}:${m === 0 ? '00' : '30'} ${period}`
        });
      }
    }
  }
  return times;
};
const timeOptions = generateTimeOptions();

// Remove TimeRangePickerModal and use manual input fields instead

const arabicMonths = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
];
const arabicNumbers = ['٠','١','٢','٣','٤','٥','٦','٧','٨','٩'];
function toArabicNumber(num) {
  return String(num).replace(/[0-9]/g, d => arabicNumbers[d]);
}
function toArabicTime(timeStr) {
  return String(timeStr)
    .replace(/[0-9]/g, d => arabicNumbers[d])
    .replace(/AM/gi, 'ص')
    .replace(/PM/gi, 'م');
}

export default function CalendarPage() {
  const location = useLocation();
  const teacher = location.state?.teacher;
  const { sidebarVisible, setSidebarVisible, isArabic } = useOutletContext?.() || {};

  // Determine user role from the route path (not from database lookup)
  // This ensures students see student calendar even if they're also registered as teachers
  const isTeacherRoute = location.pathname.includes('/dashboard/teacher/');
  // Use teacher's actual visit places from their profile, fallback to all options if not available
  // Places selection removed per request
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Handle window resize for mobile detection
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Inject CSS animations for mobile booking flow
  React.useEffect(() => {
    const styleId = 'calendar-mobile-animations';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideOutRight {
          from { opacity: 1; transform: translateX(0); }
          to { opacity: 0; transform: translateX(-30px); }
        }
        @keyframes slideOutLeft {
          from { opacity: 1; transform: translateX(0); }
          to { opacity: 0; transform: translateX(30px); }
        }
        .step-transition-enter {
          animation: slideInRight 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        .step-transition-enter-back {
          animation: slideInLeft 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        .step-transition-exit {
          animation: slideOutLeft 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
        .step-transition-exit-back {
          animation: slideOutRight 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
      `;
      document.head.appendChild(style);
    }
    return () => {
      const style = document.getElementById(styleId);
      if (style) style.remove();
    };
  }, []);
  
  const [currentMonth, setCurrentMonth] = useState(new Date()); // Start with the current month
  const [selectedDate, setSelectedDate] = useState(null);

  // Auto-select tomorrow's date when teacher is passed via navigation
  React.useEffect(() => {
    logger.debug('🔍 Calendar useEffect - teacher:', teacher);
    if (teacher) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      logger.debug('🔍 Calendar: Auto-selecting date:', tomorrow);
      setSelectedDate(tomorrow);
    }
  }, [teacher]);
  const [timeRange, setTimeRange] = useState({ from: '', to: '' });
  const [timeError, setTimeError] = useState('');
  const [studyFiles, setStudyFiles] = useState([]);
  const [numStudents, setNumStudents] = useState('');
  const [numStudentsError, setNumStudentsError] = useState('');
  const [participantEmails, setParticipantEmails] = useState([]);
  const [participantEmailsError, setParticipantEmailsError] = useState('');
  const [bookingStep, setBookingStep] = useState(1); // 1 = date/time selection, 2 = booking details
  const [mobileDetailsStep, setMobileDetailsStep] = useState(1); // Mobile: 1 = subject, 2 = session type, 3 = lesson format, 4 = final details
  const [prevBookingStep, setPrevBookingStep] = useState(1);
  const [prevMobileDetailsStep, setPrevMobileDetailsStep] = useState(1);
  const [selectedPlace, setSelectedPlace] = useState(''); // For face-to-face lessons: 'teacher_preferred', 'home', or custom
  
  // Helper to get animation class based on step direction
  const getStepAnimationClass = (currentStep, prevStep, isMobileStep = false) => {
    const stepDiff = currentStep - prevStep;
    if (stepDiff > 0) {
      return 'step-transition-enter'; // Forward
    } else if (stepDiff < 0) {
      return 'step-transition-enter-back'; // Backward
    }
    return 'step-transition-enter'; // Default to forward
  };
  const [customPlace, setCustomPlace] = useState(''); // Custom place input
  const [locationData, setLocationData] = useState(null); // Selected location with coordinates
  const [timeFormat, setTimeFormat] = useState('12h'); // 12h or 24h time format
  const [selectedHours, setSelectedHours] = useState(null); // Selected duration in hours (1-6)
  const [requestType, setRequestType] = useState(location.state?.requestType || 'individual'); // individual or group
  const [lessonFormat, setLessonFormat] = useState('online'); // online or in-person
  // Subject selection based on teacher's subjects
  const teacherSubjects = Array.isArray(teacher?.subjects) ? teacher.subjects : [];
  const [selectedSubject, setSelectedSubject] = useState('');
  // const [timeRangeModalOpen, setTimeRangeModalOpen] = useState(false); // Removed

  // Coupon/Wallet state
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  
  // Booking submission state
  const [submitting, setSubmitting] = useState(false);

  // Calculate current step number (1-6) for mobile (after all state declarations)
  const getCurrentMobileStep = () => {
    if (bookingStep === 1) {
      return selectedHours ? (timeRange.from ? 3 : 2) : 1;
    } else {
      return 3 + mobileDetailsStep; // 4, 5, or 6
    }
  };
  
  const currentMobileStep = getCurrentMobileStep();
  const prevMobileStep = React.useRef(currentMobileStep);
  
  // Track step changes for animation direction
  React.useEffect(() => {
    prevMobileStep.current = currentMobileStep;
  }, [currentMobileStep]);
  
  // Get animation class based on direction (computed value)
  const stepAnimationClass = React.useMemo(() => {
    const stepDiff = currentMobileStep - prevMobileStep.current;
    if (stepDiff > 0) {
      return 'step-transition-enter'; // Forward
    } else if (stepDiff < 0) {
      return 'step-transition-enter-back'; // Backward
    }
    return 'step-transition-enter'; // Default
  }, [currentMobileStep]);

  // Arabic translations
  const t = isArabic
    ? {
        calendar: 'التقويم',
        subtitle: 'يمكنك رؤية اجتماعاتك من خلال تقويمنا',
        refresh: 'تحديث الطلبات',
        debugInfo: 'معلومات التصحيح:',
        bookingRequests: 'طلب حجز موجود',
        selectedDate: 'التاريخ المحدد:',
        status: 'الحالة:',
        bookingDisabled: 'الحجز معطل:',
        yes: 'نعم',
        no: 'لا',
        none: 'لا شيء',
        today: 'اليوم',
        yesterday: 'أمس',
        noEvents: 'لا توجد أحداث مجدولة لهذا الشهر.',
        teacherInfo: 'معلومات المعلم',
        chooseSubject: 'اختر المادة',
        selectSubjectPlaceholder: 'اختر مادة',
        pricePerHour: 'السعر في الساعة',
        pricePerHourGroup: 'السعر في الساعة (للجماعة)',
        selectPlace: 'اختر المكان',
        selectPlaceholder: 'اختر مكان',
        numberOfStudents: 'عدد الطلاب',
        enterStudents: 'أدخل عدد الطلاب (الحد الأدنى 2)',
        atLeast2: 'يجب أن يكون هناك طالبان على الأقل للجلسات الجماعية.',
        selectedDateLabel: 'التاريخ المحدد',
        selectTime: 'اختر الوقت',
        from: 'من',
        to: 'إلى',
        fromPlaceholder: 'مثال: 6:30 مساءً',
        toPlaceholder: 'مثال: 7:30 مساءً',
        endTimeError: 'يجب أن يكون وقت الانتهاء بعد وقت البداية وكلاهما يجب أن يكون صحيحاً (مثال: 6:30 مساءً)',
        uploadMaterials: 'رفع المواد الدراسية (اختياري)',
        chooseFiles: 'اختر الملفات',
        applyCoupon: 'تطبيق كوبون',
        selectCoupon: 'اختر كوبون',
        noCoupons: 'لا توجد كوبونات متاحة',
        couponDiscount: 'خصم الكوبون',
        originalPrice: 'السعر الأصلي',
        finalPrice: 'السعر النهائي',
        removeCoupon: 'إزالة الكوبون',
        requestSession: 'طلب جلسة',
        alreadyHaveSession: 'لديك بالفعل جلسة مع هذا المعلم. بعد انتهاء الجلسة، يمكنك الحجز مرة أخرى معهم.',
        profile: 'الملف الشخصي',
        accept: 'اقبل',
        reject: 'رفض',
        remove: 'إزالة'
      }
    : {
        calendar: 'Calendar',
        subtitle: 'you can see your meetings through our calendar',
        refresh: 'Refresh Booking Requests',
        debugInfo: 'Debug Info:',
        bookingRequests: 'booking requests found',
        selectedDate: 'Selected date:',
        status: 'Status:',
        bookingDisabled: 'Booking disabled:',
        yes: 'Yes',
        no: 'No',
        none: 'none',
        today: 'Today',
        yesterday: 'Yesterday',
        noEvents: 'No events scheduled for this month.',
        teacherInfo: 'Teacher Info',
        chooseSubject: 'Choose Subject',
        selectSubjectPlaceholder: 'Select a subject',
        pricePerHour: 'Price Per Hour',
        pricePerHourGroup: 'Price Per Hour (for Group)',
        // Place selection removed
        numberOfStudents: 'Number of Students',
        enterStudents: 'Enter number of students (min 2)',
        atLeast2: 'At least 2 students required for group sessions.',
        selectedDateLabel: 'Selected Date',
        selectTime: 'Select Time',
        from: 'From',
        to: 'To',
        fromPlaceholder: 'e.g., 6:30 PM',
        toPlaceholder: 'e.g., 7:30 PM',
        endTimeError: 'End time must be after start time and both must be valid (e.g., 6:30 PM)',
        uploadMaterials: 'Upload Study Materials (optional)',
        chooseFiles: 'Choose Files',
        applyCoupon: 'Apply Coupon',
        selectCoupon: 'Select a coupon',
        noCoupons: 'No coupons available',
        couponDiscount: 'Coupon Discount',
        originalPrice: 'Original Price',
        finalPrice: 'Final Price',
        removeCoupon: 'Remove Coupon',
        requestSession: 'Request Session',
        alreadyHaveSession: 'You already have a session with this teacher. After you finish the session, you can book again with them.',
        profile: 'Profile',
        accept: 'Accept',
        reject: 'Reject',
        remove: 'Remove'
      };

  // Add today at the top so it's available everywhere
  const today = new Date();

  // Logged-in user (to support teacher view)
  const [studentName, setStudentName] = useState('');
  const [studentUserId, setStudentUserId] = useState(null);
  const [studentPreferredLocation, setStudentPreferredLocation] = useState(null);
  const [studentEmail, setStudentEmail] = useState('');
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [daySessions, setDaySessions] = useState([]); // teacher sessions for selected date
  const [loadingDaySessions, setLoadingDaySessions] = useState(false);

  // Fetch user profile on mount (works for both students and teachers)
  React.useEffect(() => {
    async function bootstrapUser() {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        setCurrentUserEmail(user.email);
        setStudentEmail(user.email); // Set studentEmail from session instead of localStorage
      }
      if (user?.id) setStudentUserId(user.id);

      // Detect user role and fetch profile from correct table
      if (user?.email) {
        logger.debug('[BOOTSTRAP] Fetching profile for email:', user.email);

        // First check if user is a teacher (use capital T like Sidebar does)
        const { data: teacherData } = await supabase
          .from('Teachers')
          .select('full_name, profile_photo')
          .eq('email', user.email)
          .maybeSingle();

        if (teacherData?.full_name) {
          setStudentName(teacherData.full_name); // Reusing studentName state
          logger.debug('[BOOTSTRAP] Found teacher profile:', teacherData.full_name);
          return;
        }

        // If not a teacher, try students table
        const { data: studentData, error } = await supabase
          .from('students')
          .select('full_name, profile_photo, preferred_location_name, preferred_location_latitude, preferred_location_longitude, preferred_location_place_id, preferred_location_formatted_address')
          .eq('email', user.email)
          .maybeSingle();

        if (error) {
          logger.warn('[BOOTSTRAP] Error fetching student profile:', error.message);
        } else if (studentData?.full_name) {
          setStudentName(studentData.full_name);
          logger.debug('[BOOTSTRAP] Found student profile:', studentData.full_name);

          // Store student preferred location if set
          if (studentData.preferred_location_formatted_address) {
            setStudentPreferredLocation({
              name: studentData.preferred_location_name || 'My Location',
              latitude: studentData.preferred_location_latitude,
              longitude: studentData.preferred_location_longitude,
              place_id: studentData.preferred_location_place_id,
              formatted_address: studentData.preferred_location_formatted_address
            });
            logger.debug('[BOOTSTRAP] Found student preferred location:', studentData.preferred_location_formatted_address);
          }
        } else {
          logger.debug('[BOOTSTRAP] No profile found');
        }
      }
    }
    bootstrapUser();
  }, []);

  // DEBUG: Log selectedDate whenever it changes
  React.useEffect(() => {
    logger.debug('[DEBUG] selectedDate state updated:', selectedDate);
  }, [selectedDate]);

  // Reset selectedDate and booking step if teacher changes
  React.useEffect(() => {
    setSelectedDate(null);
    setBookingStep(1);
    setTimeRange({ from: '', to: '' });
    setSelectedHours(null);
  }, [teacher]);
  
  // Reset booking step when date changes
  React.useEffect(() => {
    if (selectedDate) {
      setBookingStep(1);
      setTimeRange({ from: '', to: '' });
      setSelectedHours(null);
    }
  }, [selectedDate]);
  
  // Move to step 2 when time is selected
  React.useEffect(() => {
    if (selectedDate && timeRange.from && timeRange.to && bookingStep === 1) {
      // Don't auto-advance, let user click "Continue" or "Next"
    }
  }, [timeRange, selectedDate, bookingStep]);

  // Auto-set lesson format based on teacher's preference
  React.useEffect(() => {
    if (teacher?.lesson_format_preference === 'online_only') {
      setLessonFormat('online');
    } else if (teacher?.lesson_format_preference === 'face_to_face_only') {
      setLessonFormat('in-person');
    }
  }, [teacher]);

  // Fetch available coupons for the student
  React.useEffect(() => {
    async function fetchCoupons() {
      if (!studentUserId) return;

      setLoadingCoupons(true);
      try {
        const { success, coupons } = await getAvailableCoupons(studentUserId);
        if (success) {
          setAvailableCoupons(coupons || []);
          logger.debug('[CALENDAR] Fetched available coupons:', coupons);
        } else {
          setAvailableCoupons([]);
        }
      } catch (error) {
        logger.error('[CALENDAR] Error fetching coupons:', error);
        setAvailableCoupons([]);
      } finally {
        setLoadingCoupons(false);
      }
    }

    fetchCoupons();
  }, [studentUserId]);

  // Booking requests state
  const [bookingRequests, setBookingRequests] = useState([]);
  const [allUserBookings, setAllUserBookings] = useState([]); // All bookings for current user (for calendar overview)
  const [userRole, setUserRole] = useState(null); // 'student' or 'teacher'
  const [teacherBookings, setTeacherBookings] = useState([]); // All bookings for the selected teacher (to show booked times)

  // Fetch ALL bookings for the current user (for calendar overview)
  // Always fetch regardless of whether a specific teacher is selected for booking
  React.useEffect(() => {
    async function fetchAllUserBookings() {
      if (!currentUserEmail) return;

      try {
        // Determine role from route path, not database lookup
        // This ensures students see student bookings even if also registered as teachers
        const roleFromRoute = isTeacherRoute ? 'teacher' : 'student';

        logger.debug('[CALENDAR] Role determined from route:', {
          isTeacherRoute,
          roleFromRoute,
          pathname: location.pathname,
          email: currentUserEmail
        });

        if (roleFromRoute === 'teacher') {
          // User is on teacher dashboard - fetch all bookings where they are the teacher
          setUserRole('teacher');
          const { data } = await supabase
            .from('booking_requests')
            .select('*')
            .eq('teacher_email', currentUserEmail)
            .order('date', { ascending: true });
          setAllUserBookings(data || []);
          logger.debug('[CALENDAR] Fetched teacher bookings:', data);
          logger.debug('[CALENDAR] Accepted bookings:', data?.filter(b => b.status === 'accepted'));
        } else {
          // User is on student dashboard - fetch all bookings where they are the student
          // Also include group bookings where the student is a participant
          setUserRole('student');
          const { data } = await supabase
            .from('booking_requests')
            .select('*')
            .or(`student_email.eq.${currentUserEmail},participant_emails.cs.{"${currentUserEmail}"}`)
            .order('date', { ascending: true });
          setAllUserBookings(data || []);
          logger.debug('[CALENDAR] Fetched student bookings (including group participations):', data);
          logger.debug('[CALENDAR] Accepted bookings:', data?.filter(b => b.status === 'accepted'));
        }
      } catch (error) {
        logger.error('Error fetching user bookings:', error);
      }
    }

    fetchAllUserBookings();
  }, [currentUserEmail, teacher, isTeacherRoute, location.pathname]);

  // Set up real-time subscription for all user bookings
  React.useEffect(() => {
    if (!currentUserEmail || !userRole) return;

    const channel = userRole === 'teacher'
      ? `teacher_bookings_${currentUserEmail}`
      : `student_bookings_${currentUserEmail}`;

    const subscription = supabase
      .channel(channel)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'booking_requests',
          filter: userRole === 'teacher'
            ? `teacher_email=eq.${currentUserEmail}`
            : `student_email=eq.${currentUserEmail}`
        },
        async (payload) => {
          logger.debug('User booking change detected:', payload);
          // Refetch bookings on change
          let query = supabase
            .from('booking_requests')
            .select('*');

          if (userRole === 'teacher') {
            query = query.eq('teacher_email', currentUserEmail);
          } else {
            // Include own bookings and group participations
            query = query.or(`student_email.eq.${currentUserEmail},participant_emails.cs.{"${currentUserEmail}"}`);
          }

          const { data } = await query.order('date', { ascending: true });
          setAllUserBookings(data || []);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [currentUserEmail, teacher, userRole]);

  // Fetch booking requests for this student/teacher/month (when booking with specific teacher)
  React.useEffect(() => {
    async function fetchRequests() {
      if (!studentEmail || !teacher?.email) return;
      const { data } = await getStudentBookingRequests(studentEmail, teacher.email);
      setBookingRequests(data || []);
      logger.debug('Fetched booking requests:', data);
    }
    fetchRequests();

    // Add real-time subscription to update booking requests when status changes
    if (studentEmail && teacher?.email) {
      const subscription = supabase
        .channel('booking_requests_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'booking_requests',
            filter: `student_email=eq.${studentEmail} AND teacher_email=eq.${teacher.email}`
          },
          (payload) => {
            logger.debug('Booking request change detected:', payload);
            // Refresh the booking requests when any change occurs
            fetchRequests();
          }
        )
        .subscribe();

      // Cleanup subscription on unmount
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [studentEmail, teacher, currentMonth, location.key]);

  // Fetch ALL teacher bookings (to show already booked times for conflict detection)
  React.useEffect(() => {
    async function fetchTeacherBookings() {
      if (!teacher?.email) return;
      const { data, error } = await getTeacherBookingRequests(teacher.email);
      if (error) {
        logger.error('Error fetching teacher bookings:', error);
      }
      setTeacherBookings(data || []);
    }
    fetchTeacherBookings();

    // Add real-time subscription to update teacher bookings when any student books
    if (teacher?.email) {
      const subscription = supabase
        .channel('teacher_all_bookings_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'booking_requests',
            filter: `teacher_email=eq.${teacher.email}`
          },
          (payload) => {
            logger.debug('Teacher booking change detected:', payload);
            // Refresh the teacher bookings when any change occurs
            fetchTeacherBookings();
          }
        )
        .subscribe();

      // Cleanup subscription on unmount
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [teacher]);

  // Manual refresh function
  const refreshBookingRequests = async () => {
    if (!studentEmail || !teacher?.email) return;
    const { data } = await getStudentBookingRequests(studentEmail, teacher.email);
    setBookingRequests(data || []);
    logger.debug('Manually refreshed booking requests:', data);
  };

  // Helper: get YYYY-MM-DD string from Date (using local timezone) - FIXED v2
  function toYMD(dateObj) {
    // Ensure we're working with a proper Date object in local timezone
    // If dateObj was created from a string, normalize it to local timezone
    const localDate = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const result = `${year}-${month}-${day}`;

    return result;
  }
  // Helper: get request for a date
  // Always use allUserBookings to show the user's full schedule consistently
  function getRequestForDate(dateObj) {
    const dateStr = toYMD(dateObj);
    return allUserBookings?.find(r => r.date === dateStr);
  }

  // Helper: get ALL sessions for a date (to show count when multiple sessions exist)
  // Always use allUserBookings to show the user's full schedule consistently
  function getSessionsForDate(dateObj) {
    const dateStr = toYMD(dateObj);
    return allUserBookings?.filter(r => r.date === dateStr) || [];
  }

  // Load sessions for a day (works for both teachers and students)
  async function loadTeacherDaySessions(dateObj) {
    try {
      setLoadingDaySessions(true);
      setDaySessions([]);
      const dateStr = toYMD(dateObj);

      logger.debug('[LOAD_SESSIONS] Loading sessions for date:', dateStr);
      logger.debug('[LOAD_SESSIONS] Context:', {
        currentUserEmail,
        userRole,
        hasTeacher: !!teacher,
        teacherEmail: teacher?.email
      });

      // If viewing general calendar (no specific teacher), use cached allUserBookings
      if (!teacher && allUserBookings.length > 0) {
        const sessionsForDay = allUserBookings.filter(r =>
          r.date === dateStr && r.status === 'accepted' && r.payment_status === 'paid'
        );
        logger.debug('[LOAD_SESSIONS] Using cached allUserBookings (confirmed only):', sessionsForDay);
        setDaySessions(sessionsForDay);
        setLoadingDaySessions(false);
        return;
      }

      // Otherwise, fetch from database
      if (!currentUserEmail) {
        logger.warn('[LOAD_SESSIONS] No currentUserEmail, cannot load sessions');
        setLoadingDaySessions(false);
        return;
      }

      let query = supabase
        .from('booking_requests')
        .select('*')
        .eq('date', dateStr);

      // If viewing general calendar (no specific teacher), filter by user role
      if (!teacher) {
        if (!userRole) {
          logger.warn('[LOAD_SESSIONS] userRole not set yet, cannot load sessions');
          setLoadingDaySessions(false);
          return;
        }
        if (userRole === 'teacher') {
          query = query.eq('teacher_email', currentUserEmail);
          logger.debug('[LOAD_SESSIONS] Fetching teacher sessions for:', currentUserEmail);
        } else {
          // Include own bookings and group participations
          query = query.or(`student_email.eq.${currentUserEmail},participant_emails.cs.{"${currentUserEmail}"}`);
          logger.debug('[LOAD_SESSIONS] Fetching student sessions (including group participations) for:', currentUserEmail);
        }
      } else {
        // If viewing specific teacher's calendar as student, show own sessions OR group sessions where participant
        query = query
          .or(`student_email.eq.${currentUserEmail},participant_emails.cs.{"${currentUserEmail}"}`)
          .eq('teacher_email', teacher.email);
        logger.debug('[LOAD_SESSIONS] Fetching sessions between student and teacher (including group participations):', {
          student: currentUserEmail,
          teacher: teacher.email
        });
      }

      const { data, error } = await query
        .eq('status', 'accepted')
        .eq('payment_status', 'paid')
        .order('time_from', { ascending: true });

      if (error) {
        logger.error('[LOAD_SESSIONS] Error loading sessions:', error);
      } else {
        logger.debug('[LOAD_SESSIONS] Loaded confirmed sessions:', data);
        setDaySessions(data || []);
      }
    } finally {
      setLoadingDaySessions(false);
    }
  }

  // Remove request handler
  async function handleRemoveRequest(id) {
    await deleteBookingRequest(id);
    setBookingRequests(reqs => reqs.filter(r => r.id !== id));
  }

  // Handle file upload
  function handleFileChange(e) {
    setStudyFiles(prev => [...prev, ...Array.from(e.target.files)]);
  }
  // Remove a file by index
  function handleRemoveFile(idx) {
    setStudyFiles(prev => prev.filter((_, i) => i !== idx));
  }

  // Helper to validate 12-hour time with AM/PM
  function parseTime12(str) {
    const match = str.trim().match(/^(1[0-2]|0?[1-9]):([0-5][0-9]) ?([AaPp][Mm])$/);
    if (!match) return null;
    let hour = parseInt(match[1], 10);
    const minute = parseInt(match[2], 10);
    const ampm = match[3].toUpperCase();
    if (ampm === 'PM' && hour !== 12) hour += 12;
    if (ampm === 'AM' && hour === 12) hour = 0;
    return { hour, minute };
  }
  function isTimeRangeValid(from, to) {
    const t1 = parseTime12(from);
    const t2 = parseTime12(to);
    if (!t1 || !t2) return false;
    if (t2.hour < t1.hour || (t2.hour === t1.hour && t2.minute <= t1.minute)) return false;
    return true;
  }

  // Helper to calculate duration in hours (can be 1.5, 2, etc.)
  function getDurationInHours(from, to) {
    const t1 = parseTime12(from);
    const t2 = parseTime12(to);
    if (!t1 || !t2) return 0;
    return (t2.hour + t2.minute / 60) - (t1.hour + t1.minute / 60);
  }

  // Helper to get day name from date (monday, tuesday, etc.)
  function getDayName(date) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
  }

  // Helper to convert 24-hour time to 12-hour format
  function convert24To12(time24) {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  }

  // Helper to convert 12-hour time to 24-hour format
  function convert12To24(time12) {
    if (!time12) return '';
    const match = time12.trim().match(/^(1[0-2]|0?[1-9]):([0-5][0-9]) ?([AaPp][Mm])$/);
    if (!match) return '';
    let hour = parseInt(match[1], 10);
    const minute = match[2];
    const ampm = match[3].toUpperCase();
    if (ampm === 'PM' && hour !== 12) hour += 12;
    if (ampm === 'AM' && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, '0')}:${minute}`;
  }

  // Check if a date is available (has time slots or is all day, and not marked as not available)
  function isDateAvailable(date) {
    if (!teacher?.availability_schedule) return true; // If no schedule, allow all dates
    const dayName = getDayName(date);
    const dayData = teacher.availability_schedule[dayName];
    
    // Handle old format (array) and new format (object with allDay, notAvailable, and slots)
    if (dayData && typeof dayData === 'object' && ('allDay' in dayData || 'notAvailable' in dayData)) {
      // New format
      // If marked as not available, return false
      if (dayData.notAvailable) return false;
      // Otherwise check if allDay or has slots
      return dayData.allDay || (dayData.slots && dayData.slots.length > 0);
    } else if (Array.isArray(dayData)) {
      // Old format - migrate on the fly
      return dayData.length > 0;
    }
    
    return false;
  }

  // Get available time slots for a date
  function getAvailableTimeSlots(date) {
    if (!teacher?.availability_schedule || !date) return [];
    const dayName = getDayName(date);
    const dayData = teacher.availability_schedule[dayName];
    
    // Handle old format (array) and new format (object with allDay, notAvailable, and slots)
    let daySlots = [];
    let isAllDay = false;
    let isNotAvailable = false;
    
    if (dayData && typeof dayData === 'object' && ('allDay' in dayData || 'notAvailable' in dayData)) {
      // New format
      isAllDay = dayData.allDay || false;
      isNotAvailable = dayData.notAvailable || false;
      daySlots = dayData.slots || [];
    } else if (Array.isArray(dayData)) {
      // Old format
      daySlots = dayData;
    }
    
    // If not available, return empty array
    if (isNotAvailable) {
      return [];
    }
    
    // If all day, return full day range
    if (isAllDay) {
      return [{
        from: convert24To12('00:00'),
        to: convert24To12('23:59'),
        from24: '00:00',
        to24: '23:59'
      }];
    }
    
    return daySlots.map(slot => ({
      from: convert24To12(slot.from),
      to: convert24To12(slot.to),
      from24: slot.from,
      to24: slot.to
    }));
  }

  // Check if a time range is within available slots
  function isTimeRangeAvailable(date, from12, to12) {
    if (!teacher?.availability_schedule || !date || !from12 || !to12) return true;
    const availableSlots = getAvailableTimeSlots(date);
    if (availableSlots.length === 0) return false;
    
    const from24 = convert12To24(from12);
    const to24 = convert12To24(to12);
    
    // Check if the requested time range fits within any available slot
    return availableSlots.some(slot => {
      return from24 >= slot.from24 && to24 <= slot.to24;
    });
  }

  // Generate time slots based on selected hours and teacher availability
  function generateTimeSlotsForHours(date, hours) {
    if (!date || !hours) return [];

    const availableSlots = getAvailableTimeSlots(date);
    if (availableSlots.length === 0) return [];

    const generatedSlots = [];
    const hoursInMinutes = hours * 60;

    availableSlots.forEach(slot => {
      const slotStart = slot.from24.split(':').map(Number);
      const slotEnd = slot.to24.split(':').map(Number);
      const slotStartMinutes = slotStart[0] * 60 + slotStart[1];
      const slotEndMinutes = slotEnd[0] * 60 + slotEnd[1];

        // Generate slots within this availability window
        // Start from the beginning of each hour
        let currentStart = slotStartMinutes;

        // Round down to the nearest hour if not already on the hour
        const startHour = Math.floor(currentStart / 60);
        currentStart = startHour * 60;

        while (currentStart + hoursInMinutes <= slotEndMinutes) {
          const currentEnd = currentStart + hoursInMinutes;

          // Convert back to HH:MM format
          const startHour24 = Math.floor(currentStart / 60);
          const startMin24 = currentStart % 60;
          const endHour24 = Math.floor(currentEnd / 60);
          const endMin24 = currentEnd % 60;

          const start24 = `${String(startHour24).padStart(2, '0')}:${String(startMin24).padStart(2, '0')}`;
          const end24 = `${String(endHour24).padStart(2, '0')}:${String(endMin24).padStart(2, '0')}`;

          generatedSlots.push({
            from: convert24To12(start24),
            to: convert24To12(end24),
            from24: start24,
            to24: end24
          });

          // Move to next hour
          currentStart += 60;
        }
    });

    // Filter out past time slots if the selected date is today
    const today = new Date();
    const selectedDateStr = format(date, 'yyyy-MM-dd');
    const todayStr = format(today, 'yyyy-MM-dd');

    if (selectedDateStr === todayStr) {
      // Get current time in 24-hour format
      const currentHour = today.getHours();
      const currentMinute = today.getMinutes();
      const currentTimeIn24 = `${String(currentHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;

      // Filter slots to only show future times (start time must be after current time)
      return generatedSlots.filter(slot => slot.from24 > currentTimeIn24);
    }

    return generatedSlots;
  }

  // Handle booking submit (for now, just alert)
  async function handleBook() {
    if (!selectedDate || !timeRange.from || !timeRange.to) {
      alert('Please select a date and time.');
      return;
    }
    
    setSubmitting(true);

    // CRITICAL: Validate profile completeness before allowing booking AND update student name
    const { data: { user } } = await supabase.auth.getUser();
    let currentStudentProfile = null;

    if (user) {
      logger.debug('[BOOKING] Fetching student profile for email:', user.email);

      const { data: studentProfile, error: profileError } = await supabase
        .from('students')
        .select('full_name, email, phone, school, grade_levels, profile_photo')
        .eq('email', user.email)
        .maybeSingle(); // Use maybeSingle to gracefully handle 0 or 1 rows

      logger.debug('[BOOKING] Profile fetch result:', { studentProfile, profileError });

      if (profileError) {
        logger.error('[BOOKING] Profile error:', profileError);
        // If no profile found, continue with existing studentName if available
        if (profileError.code === 'PGRST116' && studentName) {
          logger.warn('[BOOKING] Profile not found, using existing studentName:', studentName);
          // Continue to booking - will use existing studentName
        } else {
          alert(isArabic
            ? `خطأ: ${profileError.message}\n\nيرجى التأكد من إكمال ملفك الشخصي في صفحة الحساب.`
            : `Error: ${profileError.message}\n\nPlease make sure your profile is complete in the Profile page.`);
          return;
        }
      }

      if (studentProfile) {
        currentStudentProfile = studentProfile;
        // IMPORTANT: Update the studentName state with fresh data from DB
        setStudentName(studentProfile.full_name || '');

        const requiredFields = [
          studentProfile.full_name,
          studentProfile.email,
          studentProfile.phone,
          studentProfile.school,
          studentProfile.grade_levels,
          studentProfile.profile_photo
        ];
        const completed = requiredFields.filter(Boolean).length;
        const completenessPercentage = Math.round((completed / requiredFields.length) * 100);

        if (completenessPercentage < 100) {
          const missingFields = [];
          if (!studentProfile.full_name) missingFields.push(isArabic ? 'الاسم الكامل' : 'Full Name');
          if (!studentProfile.phone) missingFields.push(isArabic ? 'رقم الهاتف' : 'Phone');
          if (!studentProfile.school) missingFields.push(isArabic ? 'المدرسة/الجامعة' : 'School/University');
          if (!studentProfile.grade_levels) missingFields.push(isArabic ? 'المستوى الدراسي' : 'Grade Level');
          if (!studentProfile.profile_photo) missingFields.push(isArabic ? 'الصورة الشخصية' : 'Profile Photo');

          alert(
            isArabic
              ? `⚠️ يجب إكمال ملفك الشخصي قبل الحجز\n\nالحقول المفقودة:\n• ${missingFields.join('\n• ')}\n\nيرجى الذهاب إلى صفحة الملف الشخصي لإكمال معلوماتك.`
              : `⚠️ You must complete your profile before booking\n\nMissing fields:\n• ${missingFields.join('\n• ')}\n\nPlease go to your Profile page to complete your information.`
          );
          return;
        }
      }
    }
    // Place selection removed
    if (!isTimeRangeValid(timeRange.from, timeRange.to)) {
      setTimeError(isArabic 
        ? 'يجب أن يكون وقت الانتهاء بعد وقت البداية وكلاهما يجب أن يكون صحيحاً (مثال: 6:30 مساءً)'
        : 'End time must be after start time and both must be valid (e.g., 6:30 PM)');
      return;
    }
    
    // Check for time conflicts with existing bookings
    const conflict = checkTimeConflict(timeRange.from, timeRange.to);
    if (conflict) {
      setTimeError(
        isArabic
          ? `⚠️ هذا الوقت يتعارض مع حجز موجود (${conflict.time_from} - ${conflict.time_to}). الرجاء اختيار وقت آخر.`
          : `⚠️ This time conflicts with an existing booking (${conflict.time_from} - ${conflict.time_to}). Please choose another time.`
      );
      return;
    }

    // Validate time range is within teacher's availability
    if (!isTimeRangeAvailable(selectedDate, timeRange.from, timeRange.to)) {
      const availableSlots = getAvailableTimeSlots(selectedDate);
      const slotsText = availableSlots.map(s => `${s.from} - ${s.to}`).join(', ');
      setTimeError(isArabic
        ? `الوقت المحدد غير متاح. الأوقات المتاحة: ${slotsText}`
        : `Selected time is not available. Available times: ${slotsText}`);
      return;
    }
    setTimeError('');
    
    // Validate place selection for face-to-face lessons
    if (lessonFormat === 'in-person') {
      if (!selectedPlace) {
        alert(isArabic 
          ? 'يرجى اختيار المكان للدرس وجهاً لوجه'
          : 'Please select a location for the face-to-face lesson');
        setSubmitting(false);
        return;
      }
      if (selectedPlace === 'custom' && !customPlace.trim()) {
        alert(isArabic
          ? 'يرجى إدخال العنوان أو المكان'
          : 'Please enter the address or location');
        setSubmitting(false);
        return;
      }
      // Validate student preferred location has coordinates
      if (selectedPlace === 'student_preferred' && !locationData) {
        alert(isArabic
          ? 'خطأ: بيانات الموقع المفضل غير متوفرة'
          : 'Error: Preferred location data unavailable');
        setSubmitting(false);
        return;
      }
    }
    
    if (requestType === 'group') {
      if (!numStudents || Number(numStudents) < 2) {
        setNumStudentsError('At least 2 students required for group sessions.');
        return;
      }
      setNumStudentsError('');
      
      // Validate participant emails
      const validEmails = participantEmails.filter(e => e.trim());
      if (validEmails.length !== Number(numStudents) - 1) {
        setParticipantEmailsError(`Please provide ${Number(numStudents) - 1} participant email(s) (you are included in the count).`);
        return;
      }
      
      // Verify all emails are registered
      const { data: registeredUsers, error: checkError } = await supabase
        .from('students')
        .select('email')
        .in('email', validEmails);
      
      if (checkError) {
        setParticipantEmailsError('Error verifying participant emails. Please try again.');
        return;
      }
      
      const registeredEmails = (registeredUsers || []).map(u => u.email.toLowerCase());
      const unregistered = validEmails.filter(e => !registeredEmails.includes(e.toLowerCase().trim()));
      
      if (unregistered.length > 0) {
        setParticipantEmailsError(
          isArabic 
            ? `البريد الإلكتروني التالي غير مسجل في Taealam: ${unregistered.join(', ')}. يجب على جميع المشاركين التسجيل أولاً على المنصة.`
            : `The following email(s) are not registered on Taealam: ${unregistered.join(', ')}. Please ask them to sign up at taealam.com first.`
        );
        return;
      }
      
      setParticipantEmailsError('');
    }
    // Calculate total price
    const duration = getDurationInHours(timeRange.from, timeRange.to);
    let originalPrice = 0;
    if (requestType === 'group') {
      // Check teacher's group pricing model (fixed vs per_person)
      const groupPricingModel = teacher.group_pricing_model || 'per_person';
      if (groupPricingModel === 'fixed') {
        // Fixed price for entire group
        originalPrice = (Number(teacher.group_hourly_rate) || 0) * duration;
      } else {
        // Per-person pricing (default)
        originalPrice = (Number(teacher.group_hourly_rate) || 0) * duration * (Number(numStudents) || 1);
      }
    } else {
      originalPrice = (Number(teacher.hourly_rate) || 0) * duration;
    }

    // Apply coupon discount if selected
    let totalPrice = originalPrice;
    let discountAmount = 0;
    if (selectedCoupon) {
      discountAmount = (originalPrice * selectedCoupon.discount_percentage) / 100;
      totalPrice = Math.max(0, originalPrice - discountAmount);
    }
    // --- NEW: Fetch user IDs ---
    // Get current student user ID
    const { data: { user: studentUser }, error: studentError } = await supabase.auth.getUser();
    const studentId = studentUser?.id;
    
    if (studentError || !studentId) {
      alert('Error: Please log in again to continue.');
      return;
    }
    
    // Get teacher user ID - fetch the UUID from the Teachers table
    let teacherId = null;
    
    if (teacher?.email) {
      try {
        // Fetch the teacher's UUID from the Teachers table
        const { data: teacherData, error } = await supabase
          .from('Teachers')
          .select('uuid_id')
          .eq('email', teacher.email)
          .single();
        
        if (error || !teacherData) {
          alert('Error fetching teacher information. Please try again.');
          return;
        }
        
        teacherId = teacherData.uuid_id;
      } catch (err) {
        alert('Error fetching teacher information. Please try again.');
        return;
      }
    } else {
      alert('Error: Teacher information is missing. Please try again.');
      return;
    }
    
    if (!studentId || !teacherId) {
      alert('Error: Missing user information. Please try again.');
      return;
    }
    // Use fresh student profile data
    const finalStudentName = currentStudentProfile?.full_name || studentName || 'Student';

    // Determine place value for face-to-face lessons
    let placeValue = null;
    if (lessonFormat === 'in-person') {
      if (selectedPlace === 'teacher_preferred') {
        placeValue = teacher?.preferred_place || 'Teacher\'s preferred place';
      } else if (selectedPlace === 'student_preferred') {
        // Include both name and address so teacher can see the full location
        const locationName = studentPreferredLocation?.name || 'Student\'s preferred location';
        const locationAddress = studentPreferredLocation?.formatted_address || '';
        placeValue = locationAddress ? `${locationName} - ${locationAddress}` : locationName;
      } else if (selectedPlace === 'custom') {
        placeValue = customPlace.trim();
      }
    }
    
    // Prepare booking data for Supabase
    const bookingData = {
      student_id: studentId,
      teacher_uuid: teacherId, // Use teacher_uuid column for the UUID
      student_email: studentEmail,
      teacher_email: teacher.email,
      student_name: finalStudentName,
      teacher_name: teacher.full_name,
      date: toYMD(selectedDate),
      time_from: timeRange.from,
      time_to: timeRange.to,
      original_date: toYMD(selectedDate),
      original_time_from: timeRange.from,
      original_time_to: timeRange.to,
      reschedule_count: 0,
      place: placeValue, // Include place for face-to-face lessons (backward compatibility)
      // Location details (for custom locations with map)
      location_latitude: locationData?.latitude || null,
      location_longitude: locationData?.longitude || null,
      location_place_id: locationData?.place_id || null,
      location_formatted_address: locationData?.formatted_address || null,
      num_students: requestType === 'group' ? numStudents : null,
      participant_emails: requestType === 'group' ? participantEmails.filter(e => e.trim()) : null,
      request_type: requestType, // individual or group (CORRECT: booking type, not lesson format!)
      lesson_format: lessonFormat, // online or in-person (added as separate field)
      status: 'pending',
      total_price: totalPrice,
      coupon_code: selectedCoupon ? selectedCoupon.coupon_code : null,
      subject: selectedSubject || null,
      // files: ... (optional)
    };
    // Prepare booking data for email backend (with required field names)
    const emailData = {
      teacherEmail: teacher.email,
      teacherName: teacher.full_name,
      studentEmail: studentEmail,
      studentName: finalStudentName,
      requestType: requestType, // individual or group (NOT lessonFormat!)
      date: toYMD(selectedDate),
      timeFrom: timeRange.from,
      timeTo: timeRange.to,
      place: placeValue || (lessonFormat === 'online' ? 'Online' : 'Not specified'),
      numStudents: requestType === 'group' ? numStudents : 1,
      files: studyFiles.map(f => f.name),
      totalPrice: totalPrice,
      subject: selectedSubject || undefined,
      lessonFormat: lessonFormat, // online or in-person
      participantEmails: requestType === 'group' ? participantEmails.filter(e => e.trim()) : [],
      locationFormattedAddress: locationData?.formatted_address || '',
      locationLatitude: locationData?.latitude || null,
      locationLongitude: locationData?.longitude || null,
    };
    try {
      // 1. Save to Supabase
      const { result, error } = await createBookingRequest(bookingData);
      if (error) {
        logger.error('Supabase booking insert error:', error?.message || error, error?.details || '', error?.hint || '');
        throw error;
      }
      setBookingRequests(reqs => [...reqs, result]);

      // 2. Send email notification (optional: only if Supabase save succeeded)
      // Note: We send JSON here. The backend expects JSON on /api/send-booking-request
      // Avoid duplicate notifications: createBookingRequest already triggers an email.
      // If you still want to send a second notification, uncomment the block below.
      // await fetch('http://localhost:4001/api/send-booking-request', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(emailData),
      // });

      // Show success message with platform benefits
      const successMessage = isArabic
        ? `✅ تم إرسال طلبك بنجاح!\n\n🎁 ميزات الحجز عبر المنصة:\n• 🔒 حماية الدفع\n• 🎥 تسجيلات الجلسات\n• 🤖 ملخصات الذكاء الاصطناعي\n• 📊 تتبع التقدم\n\nسيتم إشعار المعلم بطلبك.`
        : `✅ Your request has been sent!\n\n🎁 Platform Booking Benefits:\n• 🔒 Payment Protection\n• 🎥 Session Recordings\n• 🤖 AI Summaries\n• 📊 Progress Tracking\n\nYour teacher will be notified of your request.`;
      
      alert(successMessage);
      // Reset form
      setSelectedDate(null);
      setBookingStep(1);
      setTimeRange({ from: '', to: '' });
      setSelectedSubject('');
      setNumStudents('');
      setParticipantEmails([]);
      setStudyFiles([]);
      setSelectedCoupon(null);
      setSelectedPlace('');
      setCustomPlace('');
      setLessonFormat('online');
    } catch (err) {
      alert(isArabic ? 'فشل إرسال الطلب. يرجى المحاولة مرة أخرى.' : 'Failed to send request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  // Go to today's date
  const goToToday = () => {
    setCurrentMonth(new Date());
    setSelectedDate(null);
  };

  // Render header with Arabic month name and RTL
  const renderHeader = () => {
    const isCurrentMonth = currentMonth.getMonth() === today.getMonth() && currentMonth.getFullYear() === today.getFullYear();
    
    if (isArabic) {
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexDirection: 'row', direction: 'rtl' }}>
          <h2 style={{ color: '#ff7426', fontWeight: 700, fontSize: 28, margin: 0 }}>{arabicMonths[currentMonth.getMonth()]} {toArabicNumber(currentMonth.getFullYear())}</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!isCurrentMonth && (
              <button 
                onClick={goToToday} 
                style={{ 
                  background: '#ff7426', 
                  color: '#fff',
                  border: 'none', 
                  borderRadius: 8, 
                  padding: '8px 16px', 
                  fontSize: 14,
                  fontWeight: 600,
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(255,116,38,0.2)'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.background = '#f97316';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(255,116,38,0.3)';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = '#ff7426';
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(255,116,38,0.2)';
                }}
              >
                {isArabic ? 'اليوم' : 'Today'}
              </button>
            )}
            <button onClick={prevMonth} style={{ background: '#fff', border: '1px solid #e0eaff', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }} onMouseOver={e => e.currentTarget.style.background = '#f8fafc'} onMouseOut={e => e.currentTarget.style.background = '#fff'}><FaChevronRight color="#ff7426" /></button>
            <button onClick={nextMonth} style={{ background: '#fff', border: '1px solid #e0eaff', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }} onMouseOver={e => e.currentTarget.style.background = '#f8fafc'} onMouseOut={e => e.currentTarget.style.background = '#fff'}><FaChevronLeft color="#ff7426" /></button>
          </div>
        </div>
      );
    } else {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: isMobile ? 16 : 24 
        }}>
          <h2 style={{ 
            color: '#1f2937', 
            fontWeight: 600, 
            fontSize: isMobile ? 18 : 20, 
            margin: 0 
          }}>
            {format(currentMonth, 'MMMM yyyy')}
          </h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!isCurrentMonth && (
              <button 
                onClick={goToToday} 
                style={{ 
                  background: 'transparent', 
                  color: '#6b7280',
                  border: '1px solid #e5e7eb', 
                  borderRadius: 6, 
                  padding: '6px 12px', 
                  fontSize: 12,
                  fontWeight: 500,
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.background = '#f3f4f6';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                Today
              </button>
            )}
            <button 
              onClick={prevMonth} 
              style={{ 
                background: 'transparent', 
                border: '1px solid #e5e7eb', 
                borderRadius: '50%', 
                width: 32, 
                height: 32, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                cursor: 'pointer', 
                transition: 'all 0.2s' 
              }} 
              onMouseOver={e => {
                e.currentTarget.style.background = '#f3f4f6';
                e.currentTarget.style.borderColor = '#d1d5db';
              }} 
              onMouseOut={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              <FaChevronLeft color="#6b7280" size={14} />
            </button>
            <button 
              onClick={nextMonth} 
              style={{ 
                background: 'transparent', 
                border: '1px solid #e5e7eb', 
                borderRadius: '50%', 
                width: 32, 
                height: 32, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                cursor: 'pointer', 
                transition: 'all 0.2s' 
              }} 
              onMouseOver={e => {
                e.currentTarget.style.background = '#f3f4f6';
                e.currentTarget.style.borderColor = '#d1d5db';
              }} 
              onMouseOut={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              <FaChevronRight color="#6b7280" size={14} />
            </button>
          </div>
        </div>
      );
    }
  };

  // Render days of week - Cal.com style
  const renderDays = () => {
    if (isArabic) {
      const days = ['الأحد','الاثنين','الثلاثاء','الأربعاء','الخميس','الجمعة','السبت'];
      return (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: isMobile ? 4 : 8,
          marginBottom: isMobile ? 8 : 12
        }}>
          {days.map((d, i) => (
            <div key={i} style={{ 
              textAlign: 'center', 
              fontWeight: 600, 
              color: '#6b7280', 
              fontSize: isMobile ? 11 : 12,
              paddingBottom: 8,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {d}
            </div>
          ))}
        </div>
      );
    } else {
      const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
      return (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: isMobile ? 4 : 8,
          marginBottom: isMobile ? 8 : 12
        }}>
          {dayNames.map((d, i) => (
            <div key={i} style={{ 
              textAlign: 'center', 
              fontWeight: 600, 
              color: '#6b7280', 
              fontSize: isMobile ? 11 : 12,
              paddingBottom: 8,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {d}
            </div>
          ))}
        </div>
      );
    }
  };

  // Render cells - Clean and simple design
  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    const rows = [];
    let days = [];
    let day = startDate;
    
    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const currentDay = day;
        const formattedDate = isArabic ? toArabicNumber(currentDay.getDate()) : format(currentDay, "d");
        const isCurrentMonth = isSameMonth(currentDay, monthStart);
        const isSelected = selectedDate && toYMD(currentDay) === toYMD(selectedDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const currentDayNormalized = new Date(currentDay);
        currentDayNormalized.setHours(0, 0, 0, 0);
        const isPast = currentDayNormalized < today;
        const isTodayDate = toYMD(currentDay) === toYMD(today);
        const req = getRequestForDate(currentDay);
        const allDaySessions = getSessionsForDate(currentDay);
        const isAvailable = isDateAvailable(currentDay);

        // Get confirmed sessions for display (accepted AND paid)
        const confirmedSessions = allDaySessions.filter(s => s.status === 'accepted' && s.payment_status === 'paid');
        const hasSessions = confirmedSessions.length > 0;

        // Allow selection even if there's a booking - students can book multiple sessions per day
        const canSelect = isCurrentMonth && !isPast && isAvailable;
        const canViewSessions = isCurrentMonth && hasSessions;

        days.push(
          <div
            key={currentDay.toString()}
            onClick={() => {
              if (canSelect) {
                const d = new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate());
                setSelectedDate(d);
                loadTeacherDaySessions(d);
                setTimeRange({ from: '', to: '' });
              } else if (canViewSessions) {
                const d = new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate());
                setSelectedDate(d);
                loadTeacherDaySessions(d);
              } else if (!isAvailable && isCurrentMonth && !isPast) {
                alert(isArabic
                  ? 'هذا اليوم غير متاح. يرجى اختيار يوم آخر.'
                  : 'This day is not available. Please select another day.');
              }
            }}
            style={{
              padding: isMobile ? '8px 6px' : '10px 8px',
              minHeight: isMobile ? 80 : 100,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              position: 'relative',
              borderRadius: 8,
              border: isTodayDate && !isSelected ? '2px solid #ff7426' : isSelected ? '2px solid #1e293b' : '1px solid #e5e7eb',
              background: isSelected 
                ? '#f8fafc' 
                : 'transparent',
              color: !isCurrentMonth
                ? '#9ca3af'
                : isPast || !isAvailable
                  ? '#9ca3af'
                  : '#1f2937',
              fontSize: isMobile ? 13 : 14,
              fontWeight: isSelected ? 700 : (isTodayDate ? 600 : 400),
              cursor: (canSelect || canViewSessions) ? 'pointer' : 'not-allowed',
              opacity: !isCurrentMonth ? 0.4 : 1,
              transition: 'all 0.2s ease',
              fontFamily: "'IBM Plex Sans Arabic', Arial, sans-serif",
            }}
            onMouseEnter={e => {
              if ((canSelect || canViewSessions) && !isSelected && !isMobile) {
                e.currentTarget.style.background = '#f3f4f6';
                e.currentTarget.style.borderColor = '#d1d5db';
              }
            }}
            onMouseLeave={e => {
              if (!isSelected && !isMobile) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = isTodayDate ? '#ff7426' : '#e5e7eb';
              }
            }}
          >
            {/* Date Number */}
            <div style={{ 
              fontSize: isMobile ? 14 : 16,
              fontWeight: isSelected ? 700 : (isTodayDate ? 600 : 500),
              marginBottom: hasSessions ? 6 : 0,
              lineHeight: 1.2
            }}>
              {formattedDate}
            </div>
            
            {/* Session Information - Clean and Simple */}
            {hasSessions && (
              <div style={{
                width: '100%',
                fontSize: isMobile ? 10 : 11,
                lineHeight: 1.4,
                color: '#4b5563',
                marginTop: 'auto'
              }}>
                {confirmedSessions.slice(0, 1).map((session, idx) => {
                  // Determine lesson format from lesson_format field (not request_type which is individual/group)
                  const lessonFormat = session.lesson_format || 'online';
                  const isFaceToFace = lessonFormat === 'in-person' || lessonFormat === 'face-to-face' || lessonFormat === 'face_to_face';
                  
                  return (
                    <div key={idx} style={{
                      padding: '6px 8px',
                      background: '#f8fafc',
                      borderRadius: 6,
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{
                        fontSize: isMobile ? 10 : 11,
                        color: '#1f2937',
                        fontWeight: 600,
                        marginBottom: 3,
                        lineHeight: 1.3
                      }}>
                        {isArabic
                          ? `لقاء ${isFaceToFace ? 'وجهاً لوجه' : 'أونلاين'} مع ${isTeacherRoute ? (session.student_name || 'طالب') : (session.teacher_name || 'معلم')}`
                          : `Meeting ${isFaceToFace ? 'Face-to-Face' : 'Online'} with ${isTeacherRoute ? (session.student_name || 'Student') : (session.teacher_name || 'Teacher')}`}
                      </div>
                      <div style={{ 
                        fontSize: isMobile ? 9 : 10, 
                        color: '#64748b',
                        lineHeight: 1.2
                      }}>
                        {session.time_from} - {session.time_to}
                      </div>
                    </div>
                  );
                })}
                {confirmedSessions.length > 1 && (
                  <div style={{
                    marginTop: 4,
                    fontSize: isMobile ? 9 : 10,
                    color: '#6b7280',
                    fontWeight: 500,
                    textAlign: 'center'
                  }}>
                    +{confirmedSessions.length - 1} {isArabic ? 'أكثر' : 'more'}
                  </div>
                )}
              </div>
            )}
            
            {/* Show pending/declined sessions count if any */}
            {!hasSessions && allDaySessions.length > 0 && (
              <div style={{
                marginTop: 'auto',
                fontSize: isMobile ? 10 : 11,
                color: '#9ca3af',
                fontStyle: 'italic'
              }}>
                {allDaySessions.filter(s => s.status === 'pending').length > 0 && (
                  <span>{allDaySessions.filter(s => s.status === 'pending').length} {isArabic ? 'قيد الانتظار' : 'pending'}</span>
                )}
              </div>
            )}
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toString()} style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: isMobile ? 4 : 8
        }}>
          {days}
        </div>
      );
      days = [];
    }
    return <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 4 : 8 }}>{rows}</div>;
  };

  const renderAgenda = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

    const events = daysInMonth
      .map(day => {
        const dayKey = format(day, "yyyy-MM-dd");
        return mockEvents[dayKey] ? { date: day, events: mockEvents[dayKey] } : null;
      })
      .filter(Boolean); // Remove nulls

    if (events.length === 0) {
      return <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888' }}>{t.noEvents}</div>;
    }

    return (
      <div style={{ padding: '0 16px' }}>
        {events.map(({ date, events: dayEvents }) => (
          <div key={date.toString()} style={{ marginBottom: 24, borderLeft: '3px solid #ff7426', paddingLeft: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 18, color: '#232B3E' }}>{format(date, 'EEEE, MMMM d')}</div>
            {dayEvents.map((event, index) => (
              <div key={index} style={{ background: '#fff', padding: 12, borderRadius: 8, marginTop: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <span style={{ color: event.color, fontWeight: 600 }}>●</span> {event.title}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  // Render mobile calendar - matching the design image exactly
  const renderMobileCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 0 }); // Sunday start
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });
    const isCurrentMonthView = currentMonth.getMonth() === today.getMonth() && currentMonth.getFullYear() === today.getFullYear();

    // Build weeks array
    const weeks = [];
    let days = [];
    let day = startDate;
    
    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        days.push(new Date(day));
        day = addDays(day, 1);
      }
      weeks.push(days);
      days = [];
    }

    // Day headers
    const dayHeaders = isArabic 
      ? ['أ', 'إ', 'ث', 'أ', 'خ', 'ج', 'س']
      : ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    // Get sessions for selected date (or today if none selected)
    const displayDate = selectedDate || today;
    const selectedDateSessions = getSessionsForDate(displayDate).filter(s => 
      s.status === 'accepted' && s.payment_status === 'paid'
    );

    return (
      <div style={{ 
        width: '100%',
        maxWidth: 400,
        margin: '0 auto',
        padding: '0 20px'
      }}>
        {/* Page Title - Top Left */}
        <div style={{ marginTop: 16, marginBottom: 20, textAlign: isArabic ? 'right' : 'left' }}>
          <h1 style={{ 
            fontSize: 28, 
            fontWeight: 700, 
            color: '#1E293B',
            margin: 0,
            marginBottom: 8
          }}>
            {isArabic ? 'التقويم' : 'Calendar'}
          </h1>
          <p style={{ 
            fontSize: 14, 
            color: '#64748B',
            margin: 0
          }}>
            {isArabic ? 'يمكنك رؤية اجتماعاتك من خلال تقويمنا' : 'You can see your meetings through our calendar'}
          </p>
        </div>

        {/* Calendar Card */}
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: '20px 16px',
          boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
          border: '1px solid #E5E7EB'
        }}>
          {/* Month Navigation */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            marginBottom: 20,
            flexDirection: isArabic ? 'row-reverse' : 'row'
          }}>
            <button
              onClick={prevMonth}
              style={{
                background: '#F8FAFC',
                border: '1px solid #E2E8F0',
                borderRadius: 8,
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#475569'
              }}
            >
              <FaChevronLeft size={12} />
            </button>

            <div style={{
              fontWeight: 700,
              fontSize: 18,
              color: '#1E293B',
              minWidth: 150,
              textAlign: 'center'
            }}>
              {isArabic
                ? `${arabicMonths[currentMonth.getMonth()]} ${toArabicNumber(currentMonth.getFullYear())}`
                : format(currentMonth, 'MMMM yyyy')
              }
            </div>

            <button
              onClick={nextMonth}
              style={{
                background: '#F8FAFC',
                border: '1px solid #E2E8F0',
                borderRadius: 8,
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#475569'
              }}
            >
              <FaChevronRight size={12} />
            </button>
          </div>

          {/* Day Headers */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(7, 1fr)', 
            marginBottom: 8
          }}>
            {dayHeaders.map((d, i) => (
              <div key={i} style={{ 
                textAlign: 'center', 
                color: '#64748B', 
                fontWeight: 600, 
                fontSize: 13,
                padding: '8px 0'
              }}>
                {d}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 0
              }}>
                {week.map((currentDay) => {
                  const isCurrentMonth = isSameMonth(currentDay, monthStart);
                  const isSelected = selectedDate && toYMD(currentDay) === toYMD(selectedDate);
                  const isPast = currentDay < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                  const isTodayDate = toYMD(currentDay) === toYMD(today);
                  const allDaySessions = getSessionsForDate(currentDay);
                  const isAvailable = isDateAvailable(currentDay);
                  const confirmedSessions = allDaySessions.filter(s => s.status === 'accepted' && s.payment_status === 'paid');
                  const hasConfirmedSessions = confirmedSessions.length > 0;
                  const canClick = isCurrentMonth && !isPast && isAvailable;

                  return (
                    <div
                      key={currentDay.toString()}
                      onClick={() => {
                        if (isCurrentMonth && !isPast) {
                          const d = new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate());
                          setSelectedDate(d);
                          loadTeacherDaySessions(d);
                          setTimeRange({ from: '', to: '' });
                        }
                      }}
                      style={{
                        aspectRatio: '1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        background: isTodayDate && isCurrentMonth
                          ? '#FF7426' 
                          : isSelected && isCurrentMonth && !isTodayDate
                            ? '#FFF0E6'
                            : 'transparent',
                        color: isTodayDate && isCurrentMonth
                          ? '#fff' 
                          : isSelected && isCurrentMonth && !isTodayDate
                            ? '#FF7426'
                            : !isCurrentMonth
                              ? '#CBD5E1'
                              : isPast
                                ? '#94A3B8'
                                : '#1E293B',
                        fontWeight: isTodayDate || isSelected ? 600 : 500,
                        fontSize: 15,
                        cursor: isCurrentMonth && !isPast ? 'pointer' : 'default',
                        transition: 'all 0.15s ease',
                        position: 'relative',
                        border: isSelected && isCurrentMonth && !isTodayDate ? '2px solid #FF7426' : 'none'
                      }}
                    >
                      <span>{isArabic ? toArabicNumber(currentDay.getDate()) : currentDay.getDate()}</span>
                      {hasConfirmedSessions && isCurrentMonth && !isTodayDate && (
                        <span style={{ 
                          position: 'absolute', 
                          bottom: 2, 
                          width: 4, 
                          height: 4, 
                          borderRadius: '50%', 
                          background: '#FF7426'
                        }} />
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Sessions for Selected Date */}
        <div style={{ marginTop: 24 }}>
          <h2 style={{ 
            fontSize: 18, 
            fontWeight: 700, 
            color: '#1E293B',
            margin: 0,
            marginBottom: 16
          }}>
            {isArabic ? 'الجلسات' : 'Sessions'}
          </h2>

          <div style={{
            background: '#fff',
            borderRadius: 16,
            overflow: 'hidden',
            boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
            border: '1px solid #E5E7EB'
          }}>
            {/* Date Header */}
            <div style={{
              padding: '12px 16px',
              background: '#F8FAFC',
              borderBottom: '1px solid #F1F5F9',
              fontWeight: 600,
              fontSize: 14,
              color: '#475569'
            }}>
              {format(displayDate, 'EEEE, MMM d, yyyy')}
            </div>

            {selectedDateSessions.length === 0 ? (
              <div style={{
                padding: 32,
                textAlign: 'center',
                color: '#94A3B8',
                fontSize: 14
              }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                  <FaCalendarAlt size={40} color="#CBD5E1" />
                </div>
                {isArabic ? 'لا توجد جلسات في هذا اليوم' : 'No sessions on this day'}
              </div>
            ) : (
              selectedDateSessions.map((session, sessionIndex) => (
                <div 
                  key={sessionIndex}
                  style={{
                    padding: '14px 16px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    borderBottom: sessionIndex < selectedDateSessions.length - 1 
                      ? '1px solid #F1F5F9' 
                      : 'none'
                  }}
                >
                  {/* Color dot */}
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: sessionIndex % 2 === 0 ? '#FF7426' : '#3B82F6',
                    marginTop: 5,
                    flexShrink: 0
                  }} />
                  
                  {/* Meeting details */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: '#1E293B',
                      marginBottom: 2
                    }}>
                      {session.time_from} — {session.time_to}
                    </div>
                    <div style={{
                      fontSize: 13,
                      color: '#64748B'
                    }}>
                      {session.subject || (isArabic ? 'جلسة تعليمية' : 'Tutoring session')}
                    </div>
                  </div>

                  {/* Arrow */}
                  <FaChevronRight size={12} color="#CBD5E1" style={{ marginTop: 6 }} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  // Get all bookings for the selected date (not just one)
  const selectedDateBookings = selectedDate ? getSessionsForDate(selectedDate) : [];

  // Get ALL teacher's bookings for the selected date (for showing already booked times)
  const teacherBookingsForSelectedDate = selectedDate && teacher
    ? teacherBookings.filter(b => b.date === toYMD(selectedDate))
    : [];

  // Helper function to convert time string to minutes (e.g., "2:30 PM" -> 870)
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return 0;
    let [, hours, minutes, period] = match;
    hours = parseInt(hours);
    minutes = parseInt(minutes);
    if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
    if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  // Helper function to check if two time ranges overlap
  const timesOverlap = (start1, end1, start2, end2) => {
    const start1Min = timeToMinutes(start1);
    const end1Min = timeToMinutes(end1);
    const start2Min = timeToMinutes(start2);
    const end2Min = timeToMinutes(end2);
    return (start1Min < end2Min && end1Min > start2Min);
  };

  // Check if the selected time conflicts with existing bookings
  const checkTimeConflict = (fromTime, toTime) => {
    if (!fromTime || !toTime) return null;
    // Use teacherBookingsForSelectedDate to check against ALL teacher's bookings for that date
    for (const booking of teacherBookingsForSelectedDate) {
      if (booking.status === 'pending' || booking.status === 'accepted') {
        if (timesOverlap(fromTime, toTime, booking.time_from, booking.time_to)) {
          return booking;
        }
      }
    }
    return null;
  };

  // Keep the old variable for backward compatibility
  const selectedDateRequest = selectedDate ? getRequestForDate(selectedDate) : null;

  const [rescheduleState, setRescheduleState] = useState({
    open: false,
    booking: null,
    date: '',
    from: '',
    to: '',
    reason: '',
    error: '',
    success: '',
    loading: false
  });

  function openRescheduleModal(booking) {
    setRescheduleState({
      open: true,
      booking,
      date: booking?.reschedule_requested_date || booking?.date,
      from: booking?.reschedule_requested_time_from || booking?.time_from,
      to: booking?.reschedule_requested_time_to || booking?.time_to,
      reason: booking?.reschedule_reason || '',
      error: '',
      success: '',
      loading: false
    });
  }

  function closeRescheduleModal() {
    setRescheduleState(prev => ({ ...prev, open: false, booking: null, loading: false }));
  }

  async function submitRescheduleRequest(e) {
    e?.preventDefault();
    if (!rescheduleState.booking) return;
    if (!studentUserId) {
      setRescheduleState(prev => ({ ...prev, error: 'Please sign in again.', success: '' }));
      return;
    }
    if (!rescheduleState.date || !rescheduleState.from || !rescheduleState.to) {
      setRescheduleState(prev => ({ ...prev, error: 'Please choose a date and time.', success: '' }));
      return;
    }
    setRescheduleState(prev => ({ ...prev, loading: true, error: '', success: '' }));
    try {
      await requestReschedule(rescheduleState.booking.id, {
        studentId: studentUserId,
        newDate: rescheduleState.date,
        newTimeFrom: rescheduleState.from,
        newTimeTo: rescheduleState.to,
        reason: rescheduleState.reason
      });
      await refreshBookingRequests();
      setRescheduleState(prev => ({ ...prev, success: 'Reschedule request sent!', loading: false }));
      setTimeout(() => closeRescheduleModal(), 1200);
    } catch (err) {
      setRescheduleState(prev => ({ ...prev, error: err.message || 'Failed to send reschedule request.', loading: false }));
    }
  }

  return (
    <main
      style={{
        flex: 1,
        padding: isMobile ? 0 : '40px 48px',
        paddingLeft: isMobile ? 0 : (sidebarVisible ? '72px' : '48px'),
        paddingRight: isMobile ? 0 : (sidebarVisible ? '48px' : '48px'),
        minHeight: '100vh',
        background: isMobile ? '#F8FAFC' : '#f7f9fb',
        fontFamily: "'IBM Plex Sans Arabic', Arial, sans-serif",
        position: 'relative',
        transition: 'padding-left 0.3s, padding-right 0.3s',
        overflowX: 'hidden',
      }}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      {!isMobile && (
        <PageHeader 
          title={t.calendar} 
          subtitle={t.subtitle} 
          isArabic={isArabic} 
        />
      )}
      {isMobile ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 60px)',
          paddingTop: 40,
          paddingBottom: 40
        }}>
          {renderMobileCalendar()}
        </div>
      ) : (
      <div className="calendar" style={{
        background: '#fff',
        borderRadius: 16,
        padding: 24,
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
          {renderHeader()}
          {renderDays()}
          {renderCells()}
      </div>
      )}
      {/* Teacher Day Sessions Panel - Modal Popup (Desktop only) */}
      {selectedDate && !isMobile && (
        <div 
          style={{ 
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: isMobile ? '16px' : '20px',
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={() => setSelectedDate(null)}
        >
          <div 
            style={{ 
              background: '#fff', 
              borderRadius: 20, 
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)', 
              padding: 0,
              maxWidth: isMobile ? 'calc(100% - 32px)' : 550,
              width: isMobile ? 'calc(100% - 32px)' : '100%',
              maxHeight: isMobile ? '70vh' : '80vh',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              animation: 'scaleIn 0.2s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with close button */}
            <div style={{
              background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
              padding: isMobile ? '20px 16px' : '24px 28px',
              borderRadius: isMobile ? '20px 20px 0 0' : '20px 20px 0 0',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16
            }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ 
                  margin: 0, 
                  fontSize: isMobile ? 18 : 22, 
                  fontWeight: 800, 
                  color: '#fff',
                  wordBreak: 'break-word',
                  marginBottom: 4
                }}>
                  {isArabic ? 'جلسات هذا اليوم' : 'Sessions on this day'}
                </h3>
                <div style={{ 
                  color: 'rgba(255,255,255,0.9)', 
                  fontSize: isMobile ? 13 : 15,
                  fontWeight: 500,
                  marginTop: 4
                }}>
                  {isMobile 
                    ? new Date(selectedDate).toLocaleDateString(isArabic ? 'ar-BH' : 'en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                    : new Date(selectedDate).toLocaleDateString(isArabic ? 'ar-BH' : 'en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
                  }
                </div>
                {!loadingDaySessions && (
                  <div style={{
                    marginTop: 8,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'rgba(255,255,255,0.2)',
                    padding: '4px 12px',
                    borderRadius: 20,
                    fontSize: isMobile ? 12 : 13,
                    fontWeight: 600,
                    color: '#fff'
                  }}>
                    <span>{daySessions.length}</span>
                    <span>{isArabic ? 'جلسة' : daySessions.length === 1 ? 'session' : 'sessions'}</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setSelectedDate(null)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: isMobile ? 36 : 40,
                  height: isMobile ? 36 : 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: isMobile ? 20 : 24,
                  color: '#fff',
                  fontWeight: 700,
                  transition: 'background 0.2s',
                  flexShrink: 0
                }}
                onMouseEnter={e => {
                  if (!isMobile) e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                }}
                onMouseLeave={e => {
                  if (!isMobile) e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            
            {/* Content */}
            <div style={{ 
              padding: isMobile ? 20 : 28,
              flex: 1,
              overflowY: 'auto'
            }}>
              {loadingDaySessions ? (
                <div style={{ padding: isMobile ? 24 : 40, display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', justifyContent: 'center', minHeight: isMobile ? 200 : 300 }}>
                  {[1, 2, 3].map(i => (
                    <div key={i} style={{ 
                      height: isMobile ? 60 : 80, 
                      width: '100%',
                      maxWidth: 400,
                      background: '#f3f4f6', 
                      borderRadius: 12, 
                      animation: 'pulse 1.5s ease-in-out infinite' 
                    }}></div>
                  ))}
                </div>
              ) : daySessions.length === 0 ? (
                <div style={{ 
                  padding: isMobile ? 40 : 60, 
                  textAlign: 'center', 
                  color: '#64748b',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: isMobile ? 250 : 300
                }}>
                  <div style={{ 
                    fontSize: isMobile ? 64 : 80, 
                    marginBottom: isMobile ? 16 : 20,
                    filter: 'grayscale(0.3)',
                    opacity: 0.7
                  }}>📅</div>
                  <div style={{ 
                    fontSize: isMobile ? 18 : 20, 
                    fontWeight: 700,
                    color: '#1E293B',
                    marginBottom: isMobile ? 8 : 12
                  }}>
                    {isArabic ? 'لا توجد جلسات' : 'No sessions'}
                  </div>
                  <div style={{ 
                    fontSize: isMobile ? 14 : 16, 
                    color: '#64748b',
                    lineHeight: 1.6,
                    maxWidth: 300
                  }}>
                    {isArabic ? 'لا توجد جلسات مجدولة لهذا اليوم' : 'No sessions scheduled for this day'}
                  </div>
                </div>
              ) : (
                <>
                  {logger.debug('[DISPLAY] Showing sessions:', daySessions)}
                  <div style={{ display: 'grid', gap: isMobile ? 12 : 14 }}>
                  {daySessions.map((s) => {
                // Enhanced status config with payment status logic
                const getStatusConfig = () => {
                  if (s.status === 'accepted' && s.payment_status === 'paid') {
                    return { color: '#16a34a', bg: '#f0fdf4', icon: <FaCheckCircle size={isMobile ? 14 : 16} />, label: isArabic ? 'مؤكد' : 'Confirmed' };
                  } else if (s.status === 'accepted' && s.payment_status !== 'paid') {
                    return { color: '#f59e0b', bg: '#fffbeb', icon: <FaCreditCard size={isMobile ? 14 : 16} />, label: isArabic ? 'بانتظار الدفع' : 'Awaiting Payment' };
                  } else if (s.status === 'pending') {
                    return { color: '#d97706', bg: '#fff7ed', icon: <FaHourglassHalf size={isMobile ? 14 : 16} />, label: isArabic ? 'قيد الانتظار' : 'Pending' };
                  } else if (s.status === 'declined') {
                    return { color: '#dc2626', bg: '#fef2f2', icon: <FaTimesCircle size={isMobile ? 14 : 16} />, label: isArabic ? 'مرفوض' : 'Declined' };
                  }
                  return { color: '#d97706', bg: '#fff7ed', icon: <FaHourglassHalf size={isMobile ? 14 : 16} />, label: isArabic ? 'قيد الانتظار' : 'Pending' };
                };
                const config = getStatusConfig();
                
                return (
                  <div key={s.id} style={{ 
                    border: `2px solid ${config.color}20`, 
                    background: '#fff',
                    borderRadius: isMobile ? 14 : 16, 
                    padding: isMobile ? 16 : 20, 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: isMobile ? 12 : 14,
                    transition: isMobile ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={e => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = `0 8px 24px ${config.color}30`;
                      e.currentTarget.style.borderColor = config.color;
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                      e.currentTarget.style.borderColor = `${config.color}20`;
                    }
                  }}
                  >
                    {/* Status badge at top */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: 4
                    }}>
                      <div style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        borderRadius: 20,
                        background: config.bg,
                        border: `1px solid ${config.color}`,
                        fontSize: isMobile ? 12 : 13,
                        fontWeight: 700,
                        color: config.color
                      }}>
                        {config.icon}
                        <span>{config.label}</span>
                      </div>
                    </div>
                    
                    {/* Name - show teacher for students, student for teachers */}
                    <div style={{
                      fontWeight: 800,
                      color: '#1E293B',
                      fontSize: isMobile ? 18 : 20,
                      wordBreak: 'break-word',
                      marginBottom: 4
                    }}>
                      {isTeacherRoute
                        ? (s.student_name || (isArabic ? 'طالب' : 'Student'))
                        : (s.teacher_name || (isArabic ? 'معلم' : 'Teacher'))}
                    </div>
                    
                    {/* Time */}
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      color: '#64748b', 
                      fontSize: isMobile ? 14 : 15,
                      marginBottom: 8
                    }}>
                      <FaClock size={isMobile ? 16 : 18} />
                      <span style={{ fontWeight: 600, color: '#1E293B' }}>{s.time_from}</span>
                      <span style={{ color: '#cbd5e1' }}>—</span>
                      <span style={{ fontWeight: 600, color: '#1E293B' }}>{s.time_to}</span>
                    </div>
                    {/* Subject and other info */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {s.subject && (
                        <div style={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '8px 14px',
                          borderRadius: 10,
                          background: '#f1f5f9',
                          border: '1px solid #e2e8f0',
                          fontSize: isMobile ? 13 : 14,
                          fontWeight: 600,
                          color: '#475569'
                        }}>
                          <FaBook size={isMobile ? 14 : 16} />
                          <span style={{ textTransform: 'capitalize' }}>{s.subject}</span>
                        </div>
                      )}
                      <div style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '8px 14px',
                        borderRadius: 10,
                        background: '#fef3c7',
                        border: '1px solid #fbbf24',
                        fontSize: isMobile ? 13 : 14,
                        fontWeight: 600,
                        color: '#92400e',
                        textTransform: 'capitalize'
                      }}>
                        <FaClipboardList size={isMobile ? 14 : 16} />
                        <span>{s.request_type || 'individual'}</span>
                      </div>
                    </div>
                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: 8, flexDirection: isMobile ? 'column' : 'row', marginTop: 8 }}>
                      {/* Join Meeting Button */}
                      {s.meeting_link && s.status === 'accepted' && s.payment_status === 'paid' && (
                        <a
                          href={s.meeting_link}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            flex: 1,
                            padding: isMobile ? '12px 20px' : '14px 24px',
                            background: 'linear-gradient(135deg, #ff7a1a 0%, #ff9a4a 100%)',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: isMobile ? 14 : 15,
                            textDecoration: 'none',
                            borderRadius: 12,
                            border: 'none',
                            cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(255, 122, 26, 0.3)',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={e => {
                            if (!isMobile) {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 122, 26, 0.4)';
                            }
                          }}
                          onMouseLeave={e => {
                            if (!isMobile) {
                              e.currentTarget.style.transform = 'none';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 122, 26, 0.3)';
                            }
                          }}
                        >
                          <FaLink size={isMobile ? 16 : 18} />
                          <span>{isArabic ? 'انضم إلى الاجتماع' : 'Join Meeting'}</span>
                        </a>
                      )}
                      {/* Request Reschedule Button */}
                      {s.status === 'accepted' && s.reschedule_status !== 'pending' && (
                        <button
                          onClick={() => openRescheduleModal(s)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                            flex: 1,
                            padding: isMobile ? '12px 20px' : '14px 24px',
                            background: '#1E293B',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: isMobile ? 14 : 15,
                            borderRadius: 12,
                            border: 'none',
                            cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(30, 41, 59, 0.3)',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={e => {
                            if (!isMobile) {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 6px 20px rgba(30, 41, 59, 0.4)';
                              e.currentTarget.style.background = '#334155';
                            }
                          }}
                          onMouseLeave={e => {
                            if (!isMobile) {
                              e.currentTarget.style.transform = 'none';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(30, 41, 59, 0.3)';
                              e.currentTarget.style.background = '#1E293B';
                            }
                          }}
                        >
                          <FaCalendarAlt size={isMobile ? 16 : 18} />
                          <span>{isArabic ? 'إعادة جدولة' : 'Request Reschedule'}</span>
                        </button>
                      )}
                      {/* Reschedule Pending Notice */}
                      {s.reschedule_status === 'pending' && (
                        <div style={{
                          flex: 1,
                          padding: isMobile ? '12px 20px' : '14px 24px',
                          background: '#fff4e6',
                          border: '2px dashed #fbbf24',
                          borderRadius: 12,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          fontSize: isMobile ? 14 : 15,
                          fontWeight: 700,
                          color: '#b45309'
                        }}>
                          <FaHourglassHalf size={isMobile ? 16 : 18} />
                          <span>{isArabic ? 'إعادة الجدولة قيد الانتظار' : 'Reschedule Pending'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Booking UI only after date is selected - as modal popup */}
      {teacher && selectedDate && (
        <div style={{ 
          position: 'fixed', 
          inset: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: isMobile ? 'flex-end' : 'center', 
          justifyContent: 'center', 
          zIndex: 2000, 
          padding: isMobile ? 0 : 20 
        }} onClick={() => {
          setSelectedDate(null);
          setBookingStep(1);
          setTimeRange({ from: '', to: '' });
        }}>
          <div className="booking-box" style={{ 
            background: '#fff', 
            borderRadius: isMobile ? '20px 20px 0 0' : 20, 
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)', 
            padding: 0, 
            maxWidth: isMobile ? '100%' : (bookingStep === 1 ? 1200 : 600), 
            width: '100%', 
            maxHeight: isMobile ? '95vh' : '92vh', 
            overflowY: 'auto', 
            display: 'flex', 
            flexDirection: 'column', 
            position: 'relative' 
          }} onClick={(e) => e.stopPropagation()}>
            {/* Mobile Step-Based Layout */}
            {isMobile ? (
              <>
                {/* Mobile Header with Close Button */}
                <div style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0',
                  position: 'relative',
                  background: '#fff',
                  borderRadius: '20px 20px 0 0'
                }}>
                  {/* Step Indicator with Close Button */}
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: 8 }}>
                    {(() => {
                      // Calculate current mobile step (1-6)
                      let currentMobileStep = 1;
                      if (bookingStep === 1) {
                        currentMobileStep = selectedHours ? (timeRange.from ? 3 : 2) : 1;
                      } else {
                        currentMobileStep = 3 + mobileDetailsStep; // 4, 5, or 6
                      }
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                          {[1, 2, 3, 4, 5, 6].map((step) => (
                            <div
                              key={step}
                              style={{
                                width: step === currentMobileStep ? 20 : 6,
                                height: 4,
                                borderRadius: 2,
                                background: step <= currentMobileStep ? '#ff7426' : '#e2e8f0',
                                transition: 'all 0.3s ease'
                              }}
                            />
                          ))}
                          <span style={{ fontSize: 11, color: '#64748b', marginLeft: 4 }}>
                            {isArabic ? `${toArabicNumber(currentMobileStep)} من ${toArabicNumber(6)}` : `${currentMobileStep} of 6`}
                          </span>
                        </div>
                      );
                    })()}
                    <div style={{ flex: 1 }}></div>
                    <div style={{ width: 40 }}></div>
                    {/* Back Arrow or Close Button - At the end (far right) */}
                    {bookingStep === 2 ? (
                      // Step 4-6: Go back to previous step
                      <button
                        onClick={() => {
                          if (mobileDetailsStep > 1) {
                            // Go to previous detail step (e.g., step 5 -> step 4, step 6 -> step 5)
                            setMobileDetailsStep(mobileDetailsStep - 1);
                          } else {
                            // Go back to step 3 (time selection)
                            setBookingStep(1);
                          }
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: '#64748b',
                          padding: 0,
                          flexShrink: 0,
                          ...(isArabic ? { marginLeft: -170 } : { marginRight: -170 })
                        }}
                        aria-label="Back"
                      >
                        <FaChevronLeft size={16} />
                      </button>
                    ) : selectedHours && !timeRange.from ? (
                      <button
                        onClick={() => setSelectedHours(null)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: '#64748b',
                          padding: 0,
                          flexShrink: 0,
                          ...(isArabic ? { marginLeft: -170 } : { marginRight: -170 })
                        }}
                        aria-label="Back"
                      >
                        <FaChevronLeft size={16} />
                      </button>
                    ) : selectedHours && timeRange.from ? (
                      <button
                        onClick={() => setTimeRange({ from: '', to: '' })}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: '#64748b',
                          padding: 0,
                          flexShrink: 0,
                          ...(isArabic ? { marginLeft: -170 } : { marginRight: -170 })
                        }}
                        aria-label="Back"
                      >
                        <FaChevronLeft size={16} />
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedDate(null);
                          setBookingStep(1);
                          setTimeRange({ from: '', to: '' });
                          setSelectedHours(null);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          fontSize: 18,
                          color: '#94a3b8',
                          padding: 0,
                          flexShrink: 0,
                          ...(isArabic ? { marginLeft: -170 } : { marginRight: -170 })
                        }}
                        aria-label="Close"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {/* Summary Box - Only show in step 1 */}
                  {bookingStep === 1 && (
                    <div style={{
                      background: '#f8fafc',
                      borderRadius: 10,
                      padding: '10px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaCalendarAlt size={13} />
                          {isArabic ? 'اختر التاريخ والوقت' : 'Select Date & Time'}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                          {selectedDate ? format(selectedDate, 'EEE, MMM dd') : isArabic ? 'اختر تاريخاً' : 'Choose a date'}
                          {selectedHours && ` • ${selectedHours} ${selectedHours === 1 ? (isArabic ? 'ساعة' : 'hour') : (isArabic ? 'ساعات' : 'hours')}`}
                          {timeRange.from && ` • ${timeRange.from}`}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Dynamic Content Area */}
                <div style={{
                  padding: '0 16px 16px',
                  flex: 1,
                  overflowY: 'auto',
                  transition: 'all 0.3s ease'
                }}>
                  {/* Mobile Step 1-3: Calendar, Hours, Time */}
                  {bookingStep === 1 && (
                  <>
                  {/* Step 1: Calendar */}
                  {!selectedHours && (
                    <div key="step-1" className={stepAnimationClass} style={{ position: 'relative' }}>
                      {/* Calendar Header */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0 }}>
                          {isArabic ? `${arabicMonths[currentMonth.getMonth()]} ${toArabicNumber(currentMonth.getFullYear())}` : format(currentMonth, 'MMMM yyyy')}
                        </h3>
                        <div style={{ display: 'flex', gap: 8, flexDirection: isArabic ? 'row-reverse' : 'row' }}>
                          <button
                            onClick={prevMonth}
                            style={{
                              background: '#f1f5f9',
                              border: 'none',
                              borderRadius: '50%',
                              width: 32,
                              height: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                          >
                            <FaChevronLeft color="#64748b" size={12} />
                          </button>
                          <button
                            onClick={nextMonth}
                            style={{
                              background: '#f1f5f9',
                              border: 'none',
                              borderRadius: '50%',
                              width: 32,
                              height: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                          >
                            <FaChevronRight color="#64748b" size={12} />
                          </button>
                        </div>
                      </div>

                      {/* Day Headers */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 8 }}>
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                          <div key={i} style={{ textAlign: 'center', fontWeight: 600, fontSize: 11, color: '#94a3b8', padding: '4px 0' }}>
                            {day}
                          </div>
                        ))}
                      </div>

                      {/* Calendar Grid */}
                      {(() => {
                        const monthStart = startOfMonth(currentMonth);
                        const monthEnd = endOfMonth(monthStart);
                        const startDate = startOfWeek(monthStart);
                        const endDate = endOfWeek(monthEnd);
                        const days = [];
                        let day = startDate;

                        while (day <= endDate) {
                          for (let i = 0; i < 7; i++) {
                            const currentDay = day;
                            const isCurrentMonth = isSameMonth(currentDay, monthStart);
                            const isSelected = selectedDate && toYMD(currentDay) === toYMD(selectedDate);
                            const isPast = currentDay < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                            const isTodayDate = toYMD(currentDay) === toYMD(today);
                            const isAvailable = isDateAvailable(currentDay);
                            const allDaySessions = getSessionsForDate(currentDay);
                            const hasConfirmedSession = allDaySessions.some(s => s.status === 'accepted' && s.payment_status === 'paid');
                            const canSelect = isCurrentMonth && !isPast && isAvailable;

                            days.push(
                              <button
                                key={currentDay.toString()}
                                onClick={() => {
                                  if (canSelect) {
                                    const d = new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate());
                                    setSelectedDate(d);
                                    setTimeRange({ from: '', to: '' });
                                    setSelectedHours(null);
                                  }
                                }}
                                disabled={!canSelect}
                                style={{
                                  width: '100%',
                                  aspectRatio: '1',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: 8,
                                  border: isTodayDate && !isSelected ? '2px solid #ff7426' : 'none',
                                  background: isSelected ? '#1e293b' : hasConfirmedSession ? '#dcfce7' : !isAvailable || !isCurrentMonth ? '#f8fafc' : '#fff',
                                  color: isSelected ? '#fff' : !isCurrentMonth || isPast || !isAvailable ? '#cbd5e1' : '#1e293b',
                                  fontWeight: isSelected ? 700 : 500,
                                  fontSize: 13,
                                  cursor: canSelect ? 'pointer' : 'not-allowed',
                                  position: 'relative'
                                }}
                              >
                                {isArabic ? toArabicNumber(currentDay.getDate()) : currentDay.getDate()}
                                {hasConfirmedSession && !isSelected && (
                                  <span style={{ position: 'absolute', bottom: 3, width: 4, height: 4, borderRadius: '50%', background: '#22c55e' }} />
                                )}
                              </button>
                            );
                            day = addDays(day, 1);
                          }
                        }

                        return (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                            {days}
                          </div>
                        );
                      })()}

                      {/* Continue Button */}
                      {selectedDate && (
                        <button
                          onClick={() => setSelectedHours(1)}
                          style={{
                            width: '100%',
                            marginTop: 16,
                            padding: '14px',
                            background: '#1e293b',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 12,
                            fontSize: 15,
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8
                          }}
                        >
                          {isArabic ? '← التالي: اختر المدة' : 'Next: Select Duration →'}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Step 2: Hours Selection */}
                  {selectedHours && !timeRange.from && (
                    <div key="step-2" className={stepAnimationClass} style={{ position: 'relative' }}>
                      <div style={{ marginBottom: 16 }}>
                        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaClock size={16} />
                          {isArabic ? 'كم ساعة تحتاج؟' : 'How many hours?'}
                        </h3>
                        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>
                          {isArabic ? 'اختر مدة الجلسة' : 'Select session duration'}
                        </p>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                        {[1, 2, 3, 4, 5, 6].map((hours) => (
                          <button
                            key={hours}
                            onClick={() => setSelectedHours(hours)}
                            style={{
                              padding: '16px 12px',
                              borderRadius: 12,
                              border: selectedHours === hours ? '2px solid #1e293b' : '2px solid #e2e8f0',
                              background: selectedHours === hours ? '#f8fafc' : '#fff',
                              cursor: 'pointer',
                              textAlign: 'center'
                            }}
                          >
                            <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>{isArabic ? toArabicNumber(hours) : hours}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>
                              {hours === 1 ? (isArabic ? 'ساعة' : 'Hour') : (isArabic ? 'ساعات' : 'Hours')}
                            </div>
                          </button>
                        ))}
                      </div>

                      {/* Continue to Time Slots */}
                      <button
                        onClick={() => {
                          // Generate default time slots and show them
                          const availableSlots = generateTimeSlotsForHours(selectedDate, selectedHours);
                          if (availableSlots.length > 0) {
                            setTimeRange({ from: availableSlots[0].from, to: availableSlots[0].to });
                          }
                        }}
                        style={{
                          width: '100%',
                          marginTop: 20,
                          padding: '14px',
                          background: '#1e293b',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 12,
                          fontSize: 15,
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8
                        }}
                      >
                        {isArabic ? '← التالي: اختر الوقت' : 'Next: Select Time →'}
                      </button>
                    </div>
                  )}

                  {/* Step 3: Time Slots */}
                  {selectedHours && timeRange.from && (
                    <div key="step-3" className={stepAnimationClass} style={{ position: 'relative' }}>
                      <div style={{ marginBottom: 16 }}>
                        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaClock size={16} />
                          {isArabic ? 'اختر وقت البدء' : 'Select Start Time'}
                        </h3>
                        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>
                          {isArabic
                            ? `${format(selectedDate, 'EEEE')}, ${arabicMonths[selectedDate.getMonth()]} ${toArabicNumber(selectedDate.getDate())} • ${toArabicNumber(selectedHours)} ${selectedHours === 1 ? 'ساعة' : 'ساعات'}`
                            : `${format(selectedDate, 'EEEE, MMMM d')} • ${selectedHours} ${selectedHours === 1 ? 'hour' : 'hours'}`
                          }
                        </p>
                      </div>

                      {/* Time Slots */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflowY: 'auto' }}>
                        {generateTimeSlotsForHours(selectedDate, selectedHours).map((slot, idx) => {
                          // Check for booking conflicts for this slot
                          const conflict = checkTimeConflict(slot.from, slot.to);
                          
                          // Determine slot status
                          let slotStatus = 'available';
                          let isDisabled = false;
                          let isOwnPending = false;
                          
                          if (conflict) {
                            isOwnPending = conflict.student_email === currentUserEmail && 
                                          conflict.status === 'pending';
                            
                            const isOtherConfirmed = conflict.student_email !== currentUserEmail && 
                                                    conflict.status === 'accepted' && 
                                                    conflict.payment_status === 'paid';
                            
                            if (isOwnPending) {
                              slotStatus = 'own-pending';
                            } else if (isOtherConfirmed) {
                              slotStatus = 'other-confirmed';
                              isDisabled = true;
                            } else {
                              isDisabled = conflict.status === 'pending' || conflict.status === 'accepted';
                            }
                          }
                          
                          const isSelected = timeRange.from === slot.from && timeRange.to === slot.to;
                          
                          // Determine background and border colors
                          let slotBackground = isSelected ? '#f8fafc' : '#fff';
                          let slotBorder = isSelected ? '2px solid #1e293b' : '1px solid #e2e8f0';
                          let slotColor = '#1e293b';
                          
                          if (slotStatus === 'own-pending' && !isSelected) {
                            slotBackground = '#fff3cd';
                            slotBorder = '1px solid #ffc107';
                            slotColor = '#856404';
                          } else if (slotStatus === 'other-confirmed' || (conflict && isDisabled)) {
                            slotBackground = '#fee2e2';
                            slotBorder = '1px solid #ef4444';
                            slotColor = '#991b1b';
                          }
                          
                          return (
                            <button
                              key={idx}
                              disabled={isDisabled || (conflict && conflict.status === 'accepted' && conflict.payment_status === 'paid' && conflict.student_email !== currentUserEmail)}
                              onClick={() => {
                                if (isDisabled || (conflict && conflict.status === 'accepted' && conflict.payment_status === 'paid' && conflict.student_email !== currentUserEmail)) {
                                  return;
                                }
                                setTimeRange({ from: slot.from, to: slot.to });
                              }}
                              style={{
                                padding: '14px 16px',
                                borderRadius: 10,
                                border: slotBorder,
                                background: slotBackground,
                                color: slotColor,
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                opacity: isDisabled ? 0.6 : 1
                              }}
                            >
                              <span style={{ fontSize: 14, fontWeight: 600, color: slotColor }}>
                                {isArabic ? `${toArabicTime(slot.from)} - ${toArabicTime(slot.to)}` : `${slot.from} - ${slot.to}`}
                              </span>
                              {timeRange.from === slot.from && !isDisabled && (
                                <FaCheck size={16} style={{ color: '#22c55e' }} />
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Next Button - Go to Subject Selection */}
                      <button
                        onClick={() => { setBookingStep(2); setMobileDetailsStep(1); }}
                        style={{
                          width: '100%',
                          marginTop: 16,
                          padding: '14px',
                          background: '#1e293b',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 12,
                          fontSize: 15,
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        {isArabic ? '← التالي: اختر المادة' : 'Next: Select Subject →'}
                      </button>
                    </div>
                  )}
                  </>
                  )}

                  {/* Mobile Step 4-6: Booking Details */}
                  {bookingStep === 2 && (
                    <div style={{ padding: '0 16px 16px' }}>
                      {/* Step 4: Subject Selection */}
                      {mobileDetailsStep === 1 && (
                        <div key="step-4" className={stepAnimationClass} style={{ position: 'relative' }}>
                          <div style={{ textAlign: 'center', marginBottom: 16 }}>
                            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                              <FaBook size={18} />
                              {isArabic ? 'اختر المادة' : 'Select Subject'}
                            </h3>
                          </div>
                          {teacherSubjects.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                              {teacherSubjects.map((subject, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => setSelectedSubject(subject)}
                                  style={{
                                    width: '100%',
                                    padding: '14px 16px',
                                    borderRadius: 10,
                                    border: `2px solid ${selectedSubject === subject ? '#1E293B' : '#e2e8f0'}`,
                                    background: selectedSubject === subject ? '#1E293B' : '#fff',
                                    color: selectedSubject === subject ? '#fff' : '#1e293b',
                                    fontWeight: 600,
                                    fontSize: 14,
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    transition: 'all 0.2s',
                                    minHeight: 44
                                  }}
                                >
                                  {subject}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: 12, textAlign: 'center', color: '#64748b', fontSize: 14, marginBottom: 16 }}>
                              {isArabic ? 'لا توجد مواد متاحة' : 'No subjects available'}
                            </div>
                          )}
                          <button
                            onClick={() => setMobileDetailsStep(2)}
                            disabled={teacherSubjects.length > 0 && !selectedSubject}
                            style={{
                              width: '100%',
                              padding: '14px',
                              background: (teacherSubjects.length > 0 && !selectedSubject) ? '#cbd5e1' : '#1e293b',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 12,
                              fontSize: 15,
                              fontWeight: 700,
                              cursor: (teacherSubjects.length > 0 && !selectedSubject) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {isArabic ? '← التالي: نوع الجلسة' : 'Next: Session Type →'}
                          </button>
                        </div>
                      )}

                      {/* Step 5: Session Type (Individual/Group) */}
                      {mobileDetailsStep === 2 && (
                        <div key="step-5" className={stepAnimationClass} style={{ position: 'relative' }}>
                          <div style={{ textAlign: 'center', marginBottom: 16 }}>
                            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                              <FaDollarSign size={18} />
                              {isArabic ? 'نوع الجلسة' : 'Session Type'}
                            </h3>
                          </div>
                          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                            <div
                              onClick={() => setRequestType('individual')}
                              style={{
                                flex: 1,
                                background: requestType === 'individual' ? '#1E293B' : '#f1f5f9',
                                color: requestType === 'individual' ? '#fff' : '#1E293B',
                                borderRadius: 12,
                                padding: '14px 16px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                border: requestType === 'individual' ? 'none' : '2px solid #cbd5e1'
                              }}
                            >
                              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, opacity: requestType === 'individual' ? 0.9 : 0.8 }}>
                                {isArabic ? 'السعر في الساعة' : 'Price Per Hour'}
                              </div>
                              <div style={{ fontSize: 22, fontWeight: 900 }}>
                                {teacher.hourly_rate ? (isArabic ? `${toArabicNumber(teacher.hourly_rate)} BD` : `${teacher.hourly_rate} BD`) : 'N/A'}
                              </div>
                            </div>
                            <div
                              onClick={() => setRequestType('group')}
                              style={{
                                flex: 1,
                                background: requestType === 'group' ? '#FF7426' : '#fff7ed',
                                color: requestType === 'group' ? '#fff' : '#c2410c',
                                borderRadius: 12,
                                padding: '14px 16px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                border: requestType === 'group' ? 'none' : '2px solid #fed7aa'
                              }}
                            >
                              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, opacity: requestType === 'group' ? 0.9 : 0.8 }}>
                                {isArabic ? 'السعر في الساعة (للجماعة)' : 'Price Per Hour (Group)'}
                              </div>
                              <div style={{ fontSize: 22, fontWeight: 900 }}>
                                {teacher.group_hourly_rate ? (isArabic ? `${toArabicNumber(teacher.group_hourly_rate)} BD` : `${teacher.group_hourly_rate} BD`) : 'N/A'}
                              </div>
                            </div>
                          </div>

                          {/* Group: Number of Students */}
                          {requestType === 'group' && (
                            <div style={{ marginTop: 16 }}>
                              <label style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                                <FaUsers size={14} /> {isArabic ? 'عدد الطلاب' : 'Number of Students'}
                              </label>
                              <input
                                type="number"
                                min={2}
                                placeholder={isArabic ? 'أدخل عدد الطلاب (2 على الأقل)' : 'Enter number (min 2)'}
                                value={numStudents}
                                onChange={e => {
                                  const count = Number(e.target.value);
                                  setNumStudents(e.target.value);
                                  if (e.target.value && count < 2) {
                                    setNumStudentsError(isArabic ? 'مطلوب طالبان على الأقل للجلسات الجماعية.' : 'At least 2 students required.');
                                  } else {
                                    setNumStudentsError('');
                                  }
                                  // Initialize participant email array
                                  if (count >= 2) {
                                    const needed = count - 1;
                                    setParticipantEmails(prev => {
                                      const newArr = [...prev];
                                      while (newArr.length < needed) newArr.push('');
                                      return newArr.slice(0, needed);
                                    });
                                  } else {
                                    setParticipantEmails([]);
                                  }
                                }}
                                style={{
                                  width: '100%',
                                  padding: '12px 14px',
                                  borderRadius: 10,
                                  border: '2px solid #e2e8f0',
                                  fontSize: 14,
                                  background: '#fff',
                                  color: '#1e293b',
                                  fontWeight: 600,
                                  outline: 'none',
                                  minHeight: 44
                                }}
                              />
                              {numStudentsError && (
                                <div style={{ marginTop: 8, padding: '8px 12px', background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca', color: '#dc2626', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <FaExclamationTriangle size={16} />
                                  <span>{numStudentsError}</span>
                                </div>
                              )}

                              {/* Participant Emails */}
                              {numStudents >= 2 && (
                                <div style={{ marginTop: 16 }}>
                                  <label style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, display: 'block', color: '#475569' }}>
                                    {isArabic ? 'أدخل بريد المشاركين الإلكتروني' : 'Enter Participant Emails'}
                                  </label>
                                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                                    {isArabic ? `أنت الطالب الأول. أدخل ${participantEmails.length} بريد إلكتروني إضافي.` : `You are student 1. Enter ${participantEmails.length} more email(s).`}
                                  </div>
                                  {participantEmails.map((email, idx) => (
                                    <input
                                      key={idx}
                                      type="email"
                                      placeholder={isArabic ? `البريد الإلكتروني للطالب ${toArabicNumber(idx + 2)}` : `Student ${idx + 2} Email`}
                                      value={email}
                                      onChange={e => {
                                        const newEmails = [...participantEmails];
                                        newEmails[idx] = e.target.value;
                                        setParticipantEmails(newEmails);
                                      }}
                                      style={{
                                        width: '100%',
                                        padding: '12px 14px',
                                        borderRadius: 10,
                                        border: '2px solid #e2e8f0',
                                        fontSize: 14,
                                        background: '#fff',
                                        color: '#1e293b',
                                        fontWeight: 600,
                                        outline: 'none',
                                        marginBottom: 10,
                                        minHeight: 44
                                      }}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          <button
                            onClick={() => setMobileDetailsStep(3)}
                            disabled={requestType === 'group' && (!numStudents || numStudents < 2 || participantEmails.some(e => !e.trim()))}
                            style={{
                              width: '100%',
                              marginTop: 16,
                              padding: '14px',
                              background: (requestType === 'group' && (!numStudents || numStudents < 2 || participantEmails.some(e => !e.trim()))) ? '#cbd5e1' : '#1e293b',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 12,
                              fontSize: 15,
                              fontWeight: 700,
                              cursor: (requestType === 'group' && (!numStudents || numStudents < 2 || participantEmails.some(e => !e.trim()))) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {isArabic ? '← التالي: نوع الدرس' : 'Next: Lesson Format →'}
                          </button>
                        </div>
                      )}

                      {/* Step 6: Lesson Format (Online/Face-to-face) */}
                      {mobileDetailsStep === 3 && (
                        <div key="step-6" className={stepAnimationClass} style={{ position: 'relative' }}>
                          <div style={{ textAlign: 'center', marginBottom: 16 }}>
                            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                              <FaGraduationCap size={18} />
                              {isArabic ? 'نوع الدرس' : 'Lesson Format'}
                            </h3>
                          </div>
                          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                            <button
                              onClick={() => setLessonFormat('online')}
                              style={{
                                flex: 1,
                                padding: '12px 14px',
                                border: `2px solid ${lessonFormat === 'online' ? '#1E293B' : '#e5e7eb'}`,
                                background: lessonFormat === 'online' ? '#e2e8f0' : '#fff',
                                borderRadius: 10,
                                fontWeight: 600,
                                fontSize: 14,
                                cursor: 'pointer',
                                color: lessonFormat === 'online' ? '#1E293B' : '#64748b',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 6
                              }}
                            >
                              <FaLaptop size={15} />
                              {isArabic ? 'عبر الإنترنت' : 'Online'}
                            </button>
                            <button
                              onClick={() => setLessonFormat('in-person')}
                              style={{
                                flex: 1,
                                padding: '12px 14px',
                                border: `2px solid ${lessonFormat === 'in-person' ? '#1E293B' : '#e5e7eb'}`,
                                background: lessonFormat === 'in-person' ? '#e2e8f0' : '#fff',
                                borderRadius: 10,
                                fontWeight: 600,
                                fontSize: 14,
                                cursor: 'pointer',
                                color: lessonFormat === 'in-person' ? '#1E293B' : '#64748b',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 6
                              }}
                            >
                              <FaSchool size={15} />
                              {isArabic ? 'وجهاً لوجه' : 'Face-to-Face'}
                            </button>
                          </div>

                          {/* Location Selection for Face-to-Face */}
                          {lessonFormat === 'in-person' && (
                            <div style={{ marginTop: 16 }}>
                              <label style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                                <FaMapMarkerAlt size={14} /> {isArabic ? 'اختر المكان' : 'Choose Location'}
                              </label>

                              {/* Teacher's Preferred Place */}
                              {teacher?.preferred_place && (
                                <button
                                  onClick={() => {
                                    setSelectedPlace('teacher_preferred');
                                    setCustomPlace('');
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '14px 16px',
                                    marginBottom: 10,
                                    border: `2px solid ${selectedPlace === 'teacher_preferred' ? '#1E293B' : '#e2e8f0'}`,
                                    background: selectedPlace === 'teacher_preferred' ? '#e2e8f0' : '#fff',
                                    borderRadius: 10,
                                    fontWeight: 600,
                                    fontSize: 14,
                                    cursor: 'pointer',
                                    color: selectedPlace === 'teacher_preferred' ? '#1E293B' : '#64748b',
                                    textAlign: 'left',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{isArabic ? 'مكان المعلم المفضل' : "Teacher's Preferred Place"}</div>
                                  <div style={{ fontSize: 12, opacity: 0.8 }}>{teacher.preferred_place}</div>
                                </button>
                              )}

                              {/* Student's Preferred Place */}
                              {studentPreferredLocation && (
                                <button
                                  onClick={() => {
                                    setSelectedPlace('student_preferred');
                                    setCustomPlace('');
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '14px 16px',
                                    marginBottom: 10,
                                    border: `2px solid ${selectedPlace === 'student_preferred' ? '#1E293B' : '#e2e8f0'}`,
                                    background: selectedPlace === 'student_preferred' ? '#e2e8f0' : '#fff',
                                    borderRadius: 10,
                                    fontWeight: 600,
                                    fontSize: 14,
                                    cursor: 'pointer',
                                    color: selectedPlace === 'student_preferred' ? '#1E293B' : '#64748b',
                                    textAlign: 'left',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <div style={{ fontWeight: 700, marginBottom: 4 }}>{isArabic ? 'مكاني المفضل' : 'My Preferred Place'}</div>
                                  <div style={{ fontSize: 12, opacity: 0.8 }}>{studentPreferredLocation.formatted_address || studentPreferredLocation.name}</div>
                                </button>
                              )}

                              {/* Other/Custom Place */}
                              <button
                                onClick={() => setSelectedPlace('custom')}
                                style={{
                                  width: '100%',
                                  padding: '14px 16px',
                                  marginBottom: 10,
                                  border: `2px solid ${selectedPlace === 'custom' ? '#1E293B' : '#e2e8f0'}`,
                                  background: selectedPlace === 'custom' ? '#e2e8f0' : '#fff',
                                  borderRadius: 10,
                                  fontWeight: 600,
                                  fontSize: 14,
                                  cursor: 'pointer',
                                  color: selectedPlace === 'custom' ? '#1E293B' : '#64748b',
                                  textAlign: 'left',
                                  transition: 'all 0.2s'
                                }}
                              >
                                {isArabic ? 'مكان آخر' : 'Other Location'}
                              </button>

                              {/* Custom Location Input with Map */}
                              {selectedPlace === 'custom' && (
                                <div style={{ marginTop: 12 }}>
                                  <LocationPicker
                                    initialLocation={locationData ? { lat: locationData.latitude, lng: locationData.longitude } : null}
                                    onLocationSelect={(location) => {
                                      if (location) {
                                        setLocationData(location);
                                        setCustomPlace(location.formatted_address || location.place_name || '');
                                      } else {
                                        setLocationData(null);
                                        setCustomPlace('');
                                      }
                                    }}
                                    mode="single"
                                    showAutocomplete={true}
                                    showClickToSelect={true}
                                    readOnly={false}
                                    height="350px"
                                    isArabic={isArabic}
                                  />
                                </div>
                              )}
                            </div>
                          )}

                          <button
                            onClick={handleBook}
                            disabled={!selectedSubject || submitting || (lessonFormat === 'in-person' && !selectedPlace) || (lessonFormat === 'in-person' && selectedPlace === 'custom' && !customPlace.trim())}
                            style={{
                              width: '100%',
                              marginTop: 16,
                              padding: '14px',
                              background: (!selectedSubject || submitting || (lessonFormat === 'in-person' && !selectedPlace) || (lessonFormat === 'in-person' && selectedPlace === 'custom' && !customPlace.trim())) ? '#cbd5e1' : '#1E293B',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 12,
                              fontSize: 15,
                              fontWeight: 700,
                              cursor: (!selectedSubject || submitting || (lessonFormat === 'in-person' && !selectedPlace) || (lessonFormat === 'in-person' && selectedPlace === 'custom' && !customPlace.trim())) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {submitting ? (isArabic ? 'جاري الإرسال...' : 'Submitting...') : (isArabic ? 'تأكيد الحجز' : 'Confirm Booking')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* Desktop Layout - Keep Original */
              <>
                {/* Header */}
                <div style={{
                  background: '#f8fafc',
                  padding: '28px 32px',
                  borderRadius: '20px 20px 0 0',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  borderBottom: '2px solid #e2e8f0'
                }}>
                  <button
                    onClick={() => {
                      setSelectedDate(null);
                      setBookingStep(1);
                      setTimeRange({ from: '', to: '' });
                    }}
                    style={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      background: '#e2e8f0',
                      border: 'none',
                      borderRadius: '50%',
                      width: 36,
                      height: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: 20,
                      color: '#1e293b',
                      fontWeight: 700,
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={e => e.currentTarget.style.background = '#cbd5e1'}
                    onMouseOut={e => e.currentTarget.style.background = '#e2e8f0'}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                  <div style={{ color: '#1e293b', fontSize: 24, fontWeight: 800, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {bookingStep === 1 ? (
                      <>
                        <FaCalendarAlt size={22} />
                        {isArabic ? 'اختر التاريخ والوقت' : 'Select Date & Time'}
                      </>
                    ) : (
                      <>
                        <FaCalendarAlt size={22} />
                        {isArabic ? 'تفاصيل الحجز' : 'Booking Details'}
                      </>
                    )}
                  </div>
                  <div style={{ color: '#64748b', fontSize: 14, fontWeight: 500 }}>
                    {bookingStep === 1
                      ? (selectedDate ? format(selectedDate, 'EEE MMM dd yyyy') : format(new Date(), 'EEE MMM dd yyyy'))
                      : `${format(selectedDate, 'EEE MMM dd yyyy')} • ${timeRange.from} - ${timeRange.to}`}
                  </div>
                </div>

                {/* Desktop Step 1: Date & Time Selection (3-column layout) */}
                {bookingStep === 1 && (
                  <div style={{ padding: '32px', display: 'flex', flexDirection: 'row', gap: 24 }}>
                    {/* Left Column: Teacher Info */}
                    <div style={{ flex: '0 0 280px', display: 'flex', flexDirection: 'column' }}>
                      <div style={{
                        background: '#1e293b',
                        borderRadius: 12,
                        padding: '40px 24px',
                        color: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '280px',
                        gap: 20
                      }}>
                        <div style={{
                          width: 90,
                          height: 90,
                          borderRadius: '50%',
                          background: teacher.profile_photo ? 'transparent' : '#ff7426',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          border: '2px solid #ff7426',
                          flexShrink: 0
                        }}>
                          {teacher.profile_photo ? (
                            <img
                              src={teacher.profile_photo}
                              alt={teacher.full_name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <span style={{ color: '#fff', fontWeight: 700, fontSize: 32 }}>
                              {teacher.full_name?.charAt(0)?.toUpperCase() || 'T'}
                            </span>
                          )}
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontWeight: 600, fontSize: 18, color: '#fff', lineHeight: 1.4 }}>
                            {teacher.full_name}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Middle Column: Calendar */}
                    <div style={{ flex: '1', display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <h2 style={{ color: '#1e293b', fontWeight: 700, fontSize: 20, margin: 0 }}>
                          {format(currentMonth, 'MMMM yyyy')}
                        </h2>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexDirection: isArabic ? 'row-reverse' : 'row' }}>
                          <button
                            onClick={prevMonth}
                            style={{
                              background: '#fff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '50%',
                              width: 32,
                              height: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={e => e.currentTarget.style.background = '#f8fafc'}
                            onMouseOut={e => e.currentTarget.style.background = '#fff'}
                          >
                            <FaChevronLeft color="#64748b" size={12} />
                          </button>
                          <button
                            onClick={nextMonth}
                            style={{
                              background: '#fff',
                              border: '1px solid #e2e8f0',
                              borderRadius: '50%',
                              width: 32,
                              height: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={e => e.currentTarget.style.background = '#f8fafc'}
                            onMouseOut={e => e.currentTarget.style.background = '#fff'}
                          >
                            <FaChevronRight color="#64748b" size={12} />
                          </button>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, marginBottom: 8 }}>
                        {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((day, i) => (
                          <div key={i} style={{ textAlign: 'center', fontWeight: 600, fontSize: 12, color: '#64748b' }}>
                            {day}
                          </div>
                        ))}
                      </div>

                      {(() => {
                        const monthStart = startOfMonth(currentMonth);
                        const monthEnd = endOfMonth(monthStart);
                        const startDate = startOfWeek(monthStart);
                        const endDate = endOfWeek(monthEnd);
                        const days = [];
                        let day = startDate;

                        while (day <= endDate) {
                          for (let i = 0; i < 7; i++) {
                            const currentDay = day;
                            const isCurrentMonth = isSameMonth(currentDay, monthStart);
                            const isSelected = selectedDate && toYMD(currentDay) === toYMD(selectedDate);
                            const isPast = currentDay < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                            const isTodayDate = toYMD(currentDay) === toYMD(today);
                            const isAvailable = isDateAvailable(currentDay);
                            const allDaySessions = getSessionsForDate(currentDay);
                            const hasConfirmedSession = allDaySessions.some(s => s.status === 'accepted' && s.payment_status === 'paid');
                            const canSelect = isCurrentMonth && !isPast && isAvailable;

                            days.push(
                              <button
                                key={currentDay.toString()}
                                onClick={() => {
                                  if (canSelect) {
                                    const d = new Date(currentDay.getFullYear(), currentDay.getMonth(), currentDay.getDate());
                                    setSelectedDate(d);
                                    setTimeRange({ from: '', to: '' });
                                    setSelectedHours(null);
                                  }
                                }}
                                disabled={!canSelect}
                                style={{
                                  width: '100%',
                                  aspectRatio: '1',
                                  minHeight: 40,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: 8,
                                  border: isTodayDate && !isSelected ? '2px solid #ff7426' : 'none',
                                  background: isSelected ? '#1e293b' : hasConfirmedSession ? '#dcfce7' : isTodayDate ? '#fff7ed' : !isAvailable || !isCurrentMonth ? '#f8fafc' : '#fff',
                                  color: isSelected ? '#fff' : !isCurrentMonth || isPast || !isAvailable ? '#cbd5e1' : '#1e293b',
                                  fontWeight: isSelected ? 700 : (isTodayDate ? 600 : 400),
                                  fontSize: 14,
                                  cursor: canSelect ? 'pointer' : 'not-allowed',
                                  transition: 'all 0.2s',
                                  position: 'relative',
                                  padding: 0
                                }}
                                onMouseOver={e => {
                                  if (canSelect && !isSelected) {
                                    e.currentTarget.style.background = '#f1f5f9';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }
                                }}
                                onMouseOut={e => {
                                  if (canSelect && !isSelected) {
                                    e.currentTarget.style.background = '#fff';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }
                                }}
                              >
                                <span>{isArabic ? toArabicNumber(currentDay.getDate()) : currentDay.getDate()}</span>
                                {hasConfirmedSession && !isSelected && (
                                  <span style={{ position: 'absolute', bottom: 4, width: 6, height: 6, borderRadius: '50%', background: '#22c55e' }} />
                                )}
                                {isTodayDate && !isSelected && (
                                  <span style={{ fontSize: 10, color: '#ff7426', fontWeight: 600, marginTop: 2 }}>
                                    {isArabic ? 'اليوم' : 'Today'}
                                  </span>
                                )}
                              </button>
                            );
                            day = addDays(day, 1);
                          }
                        }

                        return (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
                            {days}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Right Column: Hour Selection & Time Slots */}
                    <div style={{ flex: '0 0 300px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {selectedDate && (
                        <>
                          <div style={{ marginBottom: 4 }}>
                            <div style={{ fontWeight: 700, fontSize: 18, color: '#1e293b' }}>
                              {format(selectedDate, 'EEE dd')}
                            </div>
                          </div>

                          {!selectedHours && (
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FaClock size={16} />
                                <span>{isArabic ? 'كم ساعة تحتاج؟' : 'How many hours do you need?'}</span>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                                {[1, 2, 3, 4, 5, 6].map((hours) => (
                                  <button
                                    key={hours}
                                    onClick={() => {
                                      setSelectedHours(hours);
                                      setTimeRange({ from: '', to: '' });
                                    }}
                                    style={{
                                      padding: '14px 12px',
                                      borderRadius: 12,
                                      border: '2px solid #e2e8f0',
                                      background: '#fff',
                                      color: '#1e293b',
                                      fontWeight: 700,
                                      fontSize: 14,
                                      cursor: 'pointer',
                                      transition: 'all 0.2s',
                                      textAlign: 'center',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      gap: 4
                                    }}
                                    onMouseOver={e => {
                                      e.currentTarget.style.borderColor = '#1e293b';
                                      e.currentTarget.style.background = '#f8fafc';
                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                                    }}
                                    onMouseOut={e => {
                                      e.currentTarget.style.borderColor = '#e2e8f0';
                                      e.currentTarget.style.background = '#fff';
                                      e.currentTarget.style.transform = 'translateY(0)';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}
                                  >
                                <span style={{ fontSize: 20, fontWeight: 800 }}>{isArabic ? toArabicNumber(hours) : hours}</span>
                                <span style={{ fontSize: 11, color: '#64748b' }}>
                                  {isArabic ? (hours === 1 ? 'ساعة' : 'ساعات') : (hours === 1 ? 'Hour' : 'Hours')}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Time Slots based on Selected Hours */}
                      {selectedHours && (() => {
                        const availableSlots = getAvailableTimeSlots(selectedDate);
                        const hasAvailability = availableSlots.length > 0;
                        
                        if (!hasAvailability && teacher?.availability_schedule) {
                          return (
                            <div style={{
                              padding: '16px',
                              background: '#fef2f2',
                              borderRadius: 12,
                              border: '1px solid #fca5a5',
                              color: '#991b1b',
                              fontSize: 14,
                              fontWeight: 600,
                              textAlign: 'center'
                            }}>
                              {isArabic 
                                ? '⚠️ لا توجد أوقات متاحة لهذا اليوم'
                                : '⚠️ No available times for this day'}
                            </div>
                          );
                        }
                        
                        // Generate time slots based on selected hours
                        const generatedSlots = generateTimeSlotsForHours(selectedDate, selectedHours);
                        
                        if (generatedSlots.length === 0) {
                          return (
                            <div style={{
                              padding: '16px',
                              background: '#fef2f2',
                              borderRadius: 12,
                              border: '1px solid #fca5a5',
                              color: '#991b1b',
                              fontSize: 14,
                              fontWeight: 600,
                              textAlign: 'center'
                            }}>
                              {isArabic 
                                ? `⚠️ لا توجد فترات متاحة لمدة ${selectedHours} ${selectedHours === 1 ? 'ساعة' : 'ساعات'}`
                                : `⚠️ No ${selectedHours}-hour slots available for this day`}
                            </div>
                          );
                        }
                        
                        return (
                          <>
                            {/* Selected Duration Info & Change Button */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{
                                  padding: '6px 12px',
                                  borderRadius: 8,
                                  background: '#e2e8f0',
                                  color: '#1e293b',
                                  fontWeight: 700,
                                  fontSize: 13
                                }}>
                                  {selectedHours} {isArabic ? (selectedHours === 1 ? 'ساعة' : 'ساعات') : (selectedHours === 1 ? 'Hour' : 'Hours')}
                                </div>
                                <span style={{ fontSize: 12, color: '#64748b' }}>
                                  {generatedSlots.length} {isArabic ? 'أوقات متاحة' : generatedSlots.length === 1 ? 'slot available' : 'slots available'}
                                </span>
                              </div>
                              <button
                                onClick={() => {
                                  setSelectedHours(null);
                                  setTimeRange({ from: '', to: '' });
                                }}
                                style={{
                                  padding: '6px 10px',
                                  borderRadius: 6,
                                  border: '1px solid #e2e8f0',
                                  background: '#fff',
                                  color: '#64748b',
                                  fontWeight: 600,
                                  fontSize: 11,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                                onMouseOver={e => {
                                  if (!isMobile) {
                                    e.currentTarget.style.borderColor = '#1e293b';
                                    e.currentTarget.style.background = '#f8fafc';
                                  }
                                }}
                                onMouseOut={e => {
                                  if (!isMobile) {
                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                    e.currentTarget.style.background = '#fff';
                                  }
                                }}
                              >
                                {isArabic ? 'تغيير' : 'Change'}
                              </button>
                            </div>


                            {/* Time Conflict Error Message */}
                            {timeError && (
                              <div style={{
                                padding: '12px',
                                background: '#fef2f2',
                                borderRadius: 8,
                                border: '1px solid #fca5a5',
                                marginBottom: 12,
                                fontSize: 13,
                                fontWeight: 600,
                                color: '#991b1b'
                              }}>
                                {timeError}
                              </div>
                            )}

                            {/* Generated Time Slots */}
                            <div style={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              gap: 8,
                              maxHeight: isMobile ? '300px' : '400px',
                              overflowY: 'auto',
                              padding: '4px'
                            }}>
                              {generatedSlots.map((slot, idx) => {
                                const isSelected = timeRange.from === slot.from && timeRange.to === slot.to;
                                const displayFrom = timeFormat === '24h' ? slot.from24 : slot.from;
                                const displayTo = timeFormat === '24h' ? slot.to24 : slot.to;
                                
                                // Check for booking conflicts for this slot
                                const conflict = checkTimeConflict(slot.from, slot.to);
                                
                                // Determine slot status
                                let slotStatus = 'available'; // available, own-pending, other-confirmed
                                let isDisabled = false;
                                let isOwnPending = false;
                                
                                if (conflict) {
                                  // Check if this is the current student's pending booking
                                  isOwnPending = conflict.student_email === currentUserEmail && 
                                                conflict.status === 'pending';
                                  
                                  // Check if this is another student's confirmed booking
                                  const isOtherConfirmed = conflict.student_email !== currentUserEmail && 
                                                          conflict.status === 'accepted' && 
                                                          conflict.payment_status === 'paid';
                                  
                                  if (isOwnPending) {
                                    slotStatus = 'own-pending';
                                  } else if (isOtherConfirmed) {
                                    slotStatus = 'other-confirmed';
                                    isDisabled = true;
                                  } else {
                                    // Other pending or accepted but not paid - can't book
                                    isDisabled = conflict.status === 'pending' || conflict.status === 'accepted';
                                  }
                                }
                                
                                // Determine background color based on status
                                let slotBackground = isSelected ? '#1e293b' : '#f1f5f9';
                                let slotColor = isSelected ? '#fff' : '#64748b';
                                
                                if (slotStatus === 'own-pending' && !isSelected) {
                                  slotBackground = '#fff3cd'; // Yellow for own pending
                                  slotColor = '#856404';
                                } else if (slotStatus === 'other-confirmed' || (conflict && isDisabled)) {
                                  slotBackground = '#fee2e2'; // Light red for others' confirmed
                                  slotColor = '#991b1b';
                                }
                                
                                return (
                                  <button
                                    key={idx}
                                    disabled={isDisabled || (conflict && conflict.status === 'accepted' && conflict.payment_status === 'paid' && conflict.student_email !== currentUserEmail)}
                                    onClick={() => {
                                      // Prevent booking disabled slots
                                      if (isDisabled || (conflict && conflict.status === 'accepted' && conflict.payment_status === 'paid' && conflict.student_email !== currentUserEmail)) {
                                        return;
                                      }
                                      
                                      // Check for time conflicts before setting
                                      if (conflict && !isOwnPending) {
                                        setTimeError(
                                          isArabic
                                            ? `⚠️ هذا الوقت يتعارض مع حجز موجود (${toArabicTime(conflict.time_from)} - ${toArabicTime(conflict.time_to)}). الرجاء اختيار وقت آخر.`
                                            : `⚠️ This time conflicts with an existing booking (${conflict.time_from} - ${conflict.time_to}). Please choose another time.`
                                        );
                                      } else {
                                        setTimeRange({ from: slot.from, to: slot.to });
                                        setTimeError('');
                                      }
                                    }}
                                    style={{
                                      padding: '12px 16px',
                                      borderRadius: 8,
                                      border: 'none',
                                      background: slotBackground,
                                      color: slotColor,
                                      fontWeight: 600,
                                      fontSize: 14,
                                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                                      transition: 'all 0.2s',
                                      textAlign: 'left',
                                      width: '100%',
                                      opacity: isDisabled ? 0.6 : 1
                                    }}
                                    onMouseOver={e => {
                                      if (!isSelected && !isMobile && !isDisabled) {
                                        e.currentTarget.style.background = slotStatus === 'own-pending' ? '#fff8e1' : 
                                                                          slotStatus === 'other-confirmed' ? '#fee2e2' : 
                                                                          '#e2e8f0';
                                        e.currentTarget.style.color = slotStatus === 'own-pending' ? '#856404' :
                                                                      slotStatus === 'other-confirmed' ? '#991b1b' :
                                                                      '#1e293b';
                                      }
                                    }}
                                    onMouseOut={e => {
                                      if (!isSelected && !isMobile && !isDisabled) {
                                        e.currentTarget.style.background = slotBackground;
                                        e.currentTarget.style.color = slotColor;
                                      }
                                    }}
                                  >
                                    {isArabic ? `${toArabicTime(displayFrom)} - ${toArabicTime(displayTo)}` : `${displayFrom} - ${displayTo}`}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        );
                      })()}
                      
                      {/* Continue Button */}
                      {timeRange.from && timeRange.to && (
                        <button
                          onClick={() => { setBookingStep(2); setMobileDetailsStep(1); }}
                          style={{
                            marginTop: 'auto',
                            padding: '14px 20px',
                            borderRadius: 12,
                            border: 'none',
                            background: '#1E293B',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: 15,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            width: '100%'
                          }}
                          onMouseOver={e => {
                            if (!isMobile) {
                              e.currentTarget.style.background = '#334155';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }
                          }}
                          onMouseOut={e => {
                            if (!isMobile) {
                              e.currentTarget.style.background = '#1E293B';
                              e.currentTarget.style.transform = 'none';
                            }
                          }}
                        >
                          {isArabic ? '← التالي' : 'Continue →'}
                        </button>
                      )}
                    </>
                  )}
                  
                  {!selectedDate && (
                    <div style={{
                      padding: '24px',
                      textAlign: 'center',
                      color: '#64748b',
                      fontSize: 14
                    }}>
                      {isArabic ? 'اختر تاريخاً لعرض الأوقات المتاحة' : 'Select a date to view available times'}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Step 2: Booking Details Form */}
            {bookingStep === 2 && (
            <div style={{ padding: isMobile ? '20px 16px' : '32px', display: 'flex', flexDirection: 'column', gap: isMobile ? 16 : 24 }}>
              {/* Mobile Step Indicator for Details Steps */}
              {isMobile && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {[1, 2, 3, 4, 5, 6].map((step) => (
                      <div
                        key={step}
                        style={{
                          width: step === (3 + mobileDetailsStep) ? 20 : 6,
                          height: 4,
                          borderRadius: 2,
                          background: step <= (3 + mobileDetailsStep) ? '#ff7426' : '#e2e8f0',
                          transition: 'all 0.3s ease'
                        }}
                      />
                    ))}
                    <span style={{ fontSize: 11, color: '#64748b', marginLeft: 4 }}>
                      {isArabic ? `${toArabicNumber(3 + mobileDetailsStep)} من ${toArabicNumber(6)}` : `${3 + mobileDetailsStep} of 6`}
                    </span>
                  </div>
                </div>
              )}
              {/* Back Button - Mobile: go to previous detail step, Desktop: go to step 1 */}
              <button
                onClick={() => {
                  if (isMobile && mobileDetailsStep > 1) {
                    setMobileDetailsStep(mobileDetailsStep - 1);
                  } else {
                    setBookingStep(1);
                    setMobileDetailsStep(1);
                  }
                }}
                style={{
                  alignSelf: 'flex-start',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: '2px solid #e2e8f0',
                  background: '#fff',
                  color: '#1e293b',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => {
                  if (!isMobile) {
                    e.currentTarget.style.borderColor = '#1E293B';
                    e.currentTarget.style.background = '#f8fafc';
                  }
                }}
                onMouseOut={e => {
                  if (!isMobile) {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.background = '#fff';
                  }
                }}
              >
                ← {isArabic ? 'رجوع' : 'Back'}
              </button>

              {/* Mobile Step 4: Subject Selection */}
              {(!isMobile || mobileDetailsStep === 1) && (
                <>
                  {/* Step Title for Mobile */}
                  {isMobile && (
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        <FaBook size={18} />
                        {isArabic ? 'اختر المادة' : 'Select Subject'}
                      </h3>
                    </div>
                  )}
                  {teacherSubjects.length > 0 ? (
                    <div style={{ width: '100%' }}>
                      {!isMobile && (
                        <label style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                          <FaBook size={15} /> {t.chooseSubject}
                        </label>
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 10 : 12 }}>
                        {teacherSubjects.map((subject, idx) => (
                          <button
                            key={idx}
                            onClick={() => setSelectedSubject(subject)}
                            style={{
                              width: '100%',
                              padding: isMobile ? '14px 16px' : '16px 18px',
                              borderRadius: isMobile ? 10 : 12,
                              border: `2px solid ${selectedSubject === subject ? '#1E293B' : '#e2e8f0'}`,
                              background: selectedSubject === subject ? '#1E293B' : '#fff',
                              color: selectedSubject === subject ? '#fff' : '#1e293b',
                              fontWeight: 600,
                              fontSize: isMobile ? 14 : 15,
                              cursor: 'pointer',
                              textAlign: 'left',
                              transition: 'all 0.2s',
                              minHeight: isMobile ? 44 : 'auto'
                            }}
                            onMouseOver={e => {
                              if (!isMobile && selectedSubject !== subject) {
                                e.currentTarget.style.borderColor = '#1E293B';
                                e.currentTarget.style.background = '#f8fafc';
                              }
                            }}
                            onMouseOut={e => {
                              if (!isMobile && selectedSubject !== subject) {
                                e.currentTarget.style.borderColor = '#e2e8f0';
                                e.currentTarget.style.background = '#fff';
                              }
                            }}
                          >
                            {subject}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      padding: isMobile ? '16px' : '20px',
                      background: '#f8fafc',
                      borderRadius: 12,
                      textAlign: 'center',
                      color: '#64748b',
                      fontSize: isMobile ? 14 : 15
                    }}>
                      {isArabic ? 'لا توجد مواد متاحة' : 'No subjects available'}
                    </div>
                  )}
                  {/* Mobile Next Button - Go to Session Type */}
                  {isMobile && (
                    <button
                      onClick={() => setMobileDetailsStep(2)}
                      disabled={teacherSubjects.length > 0 && !selectedSubject}
                      style={{
                        width: '100%',
                        marginTop: 16,
                        padding: '14px',
                        background: (teacherSubjects.length > 0 && !selectedSubject) ? '#cbd5e1' : '#1e293b',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 12,
                        fontSize: 15,
                        fontWeight: 700,
                        cursor: (teacherSubjects.length > 0 && !selectedSubject) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isArabic ? '← التالي: نوع الجلسة' : 'Next: Session Type →'}
                    </button>
                  )}
                </>
              )}
          {/* Mobile Step 5: Session Type (Individual/Group) */}
          {(!isMobile || mobileDetailsStep === 2) && (
            <>
              {/* Step Title for Mobile */}
              {isMobile && (
                <div style={{ textAlign: 'center', marginBottom: 8 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <FaDollarSign size={18} />
                    {isArabic ? 'نوع الجلسة' : 'Session Type'}
                  </h3>
                </div>
              )}
              {/* Price Options */}
              <div style={{ width: '100%' }}>
                {!isMobile && (
                  <label style={{ fontWeight: 700, fontSize: 15, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                    <FaDollarSign size={15} /> {isArabic ? 'الأسعار' : 'Pricing'}
                  </label>
                )}
            <div style={{ display: 'flex', gap: isMobile ? 8 : 12, width: '100%' }}>
              <div
                onClick={() => setRequestType('individual')}
                style={{
                  flex: 1,
                  background: requestType === 'individual' ? '#1E293B' : '#f1f5f9',
                  color: requestType === 'individual' ? '#fff' : '#1E293B',
                  borderRadius: isMobile ? 12 : 14,
                  padding: isMobile ? '14px 16px' : '18px 20px',
                  textAlign: 'center',
                  boxShadow: requestType === 'individual' ? '0 4px 12px rgba(30,41,59,0.3)' : '0 2px 6px rgba(0,0,0,0.05)',
                  transition: isMobile ? 'none' : 'all 0.2s',
                  border: requestType === 'individual' ? 'none' : '2px solid #cbd5e1',
                  cursor: 'pointer'
                }}>
                <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, marginBottom: isMobile ? 4 : 6, opacity: requestType === 'individual' ? 0.9 : 0.8 }}>{t.pricePerHour}</div>
                <div style={{ fontSize: isMobile ? 22 : 26, fontWeight: 900 }}>{teacher.hourly_rate ? (isArabic ? `${toArabicNumber(teacher.hourly_rate)} BD` : `${teacher.hourly_rate} BD`) : 'N/A'}</div>
              </div>
              <div
                onClick={() => setRequestType('group')}
                style={{
                  flex: 1,
                  background: requestType === 'group' ? '#FF7426' : '#fff7ed',
                  color: requestType === 'group' ? '#fff' : '#c2410c',
                  borderRadius: isMobile ? 12 : 14,
                  padding: isMobile ? '14px 16px' : '18px 20px',
                  textAlign: 'center',
                  boxShadow: requestType === 'group' ? '0 4px 12px rgba(255,116,38,0.3)' : '0 2px 6px rgba(0,0,0,0.05)',
                  transition: isMobile ? 'none' : 'all 0.2s',
                  border: requestType === 'group' ? 'none' : '2px solid #fed7aa',
                  cursor: 'pointer'
                }}>
                <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, marginBottom: isMobile ? 4 : 6, opacity: requestType === 'group' ? 0.9 : 0.8 }}>{t.pricePerHourGroup}</div>
                <div style={{ fontSize: isMobile ? 22 : 26, fontWeight: 900 }}>{teacher.group_hourly_rate ? (isArabic ? `${toArabicNumber(teacher.group_hourly_rate)} BD` : `${teacher.group_hourly_rate} BD`) : 'N/A'}</div>
                {teacher.group_hourly_rate && (
                  <div style={{ fontSize: isMobile ? 9 : 10, marginTop: isMobile ? 2 : 4, opacity: 0.8, fontWeight: 600 }}>
                    {teacher.group_pricing_model === 'fixed'
                      ? (isArabic ? 'سعر ثابت للمجموعة' : 'Fixed for group')
                      : (isArabic ? 'لكل طالب' : 'Per student')}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Group Pricing Model Info */}
          {requestType === 'group' && teacher.group_hourly_rate && (
            <div style={{
              background: teacher.group_pricing_model === 'fixed' ? '#e0f2fe' : '#fef3e8',
              borderRadius: isMobile ? 10 : 12,
              padding: isMobile ? 12 : 16,
              border: teacher.group_pricing_model === 'fixed' ? '1px solid #7dd3fc' : '1px solid #fcd34d',
              marginBottom: isMobile ? 6 : 8
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: isMobile ? 8 : 10 }}>
                <span style={{ fontSize: isMobile ? 16 : 18, flexShrink: 0 }}>
                  {teacher.group_pricing_model === 'fixed' ? <FaDollarSign size={isMobile ? 16 : 18} /> : <FaUsers size={isMobile ? 16 : 18} />}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: isMobile ? 12 : 13, color: teacher.group_pricing_model === 'fixed' ? '#075985' : '#92400e', marginBottom: isMobile ? 4 : 6 }}>
                    {teacher.group_pricing_model === 'fixed'
                      ? (isArabic ? 'سعر ثابت للمجموعة' : 'Fixed Group Price')
                      : (isArabic ? 'التسعير حسب عدد الطلاب' : 'Per-Student Pricing')}
                  </div>
                  <div style={{ fontSize: isMobile ? 11 : 12, color: teacher.group_pricing_model === 'fixed' ? '#0c4a6e' : '#78350f', lineHeight: 1.5, wordBreak: 'break-word' }}>
                    {teacher.group_pricing_model === 'fixed'
                      ? (isArabic
                        ? `السعر الإجمالي ثابت عند ${teacher.group_hourly_rate} BD للساعة بغض النظر عن عدد الطلاب في المجموعة.`
                        : `Total price is fixed at ${teacher.group_hourly_rate} BD per hour regardless of the number of students in the group.`)
                      : (isArabic
                        ? `السعر ${teacher.group_hourly_rate} BD للساعة لكل طالب. سيتم ضرب السعر في عدد الطلاب.`
                        : `The price is ${teacher.group_hourly_rate} BD per hour per student. The total will be multiplied by the number of students.`)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Terms Reminder */}
          <div style={{
            background: '#fef3c7',
            borderRadius: isMobile ? 10 : 12,
            padding: isMobile ? 12 : 16,
            border: '1px solid #fcd34d',
            marginBottom: isMobile ? 6 : 8
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: isMobile ? 8 : 10 }}>
              <FaClipboardList size={isMobile ? 16 : 18} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: isMobile ? 12 : 13, color: '#92400e', marginBottom: isMobile ? 4 : 6 }}>
                  {isArabic ? 'تذكير بشروط المنصة' : 'Platform Terms Reminder'}
                </div>
                <div style={{ fontSize: isMobile ? 11 : 12, color: '#78350f', lineHeight: 1.5, wordBreak: 'break-word' }}>
                  {isArabic 
                    ? 'جميع الحجوزات يجب أن تتم عبر المنصة. الحجوزات خارج المنصة لا تشمل الحماية أو الميزات الحصرية.'
                    : 'All bookings must be made through the platform. Off-platform bookings do not include protection or exclusive features.'}
                </div>
                <a 
                  href={isArabic ? '/terms-ar' : '/terms'} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    fontSize: isMobile ? 10 : 11, 
                    color: '#b45309', 
                    textDecoration: 'underline',
                    fontWeight: 600,
                    marginTop: 4,
                    display: 'inline-block'
                  }}
                >
                  {isArabic ? 'اقرأ الشروط الكاملة' : 'Read full terms'}
                </a>
              </div>
            </div>
          </div>
          
          {/* Place selection removed */}
          {/* Number of students for group */}
          {requestType === 'group' && (
            <div style={{ width: '100%' }}>
              <label style={{ fontWeight: 700, fontSize: isMobile ? 14 : 15, marginBottom: isMobile ? 6 : 8, display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                <FaUsers size={15} /> {t.numberOfStudents}
              </label>
              <input
                type="number"
                min={2}
                placeholder={t.enterStudents}
                value={numStudents}
                onChange={e => {
                  const count = Number(e.target.value);
                  setNumStudents(e.target.value);
                  if (e.target.value && count < 2) {
                    setNumStudentsError(t.atLeast2);
                  } else {
                    setNumStudentsError('');
                  }
                  
                  // Initialize participant email array (count - 1 because the booker is included)
                  if (count >= 2) {
                    const needed = count - 1;
                    setParticipantEmails(prev => {
                      const newArr = [...prev];
                      while (newArr.length < needed) newArr.push('');
                      return newArr.slice(0, needed);
                    });
                  } else {
                    setParticipantEmails([]);
                  }
                }}
                style={{ 
                  width: '100%', 
                  padding: isMobile ? '12px 14px' : '14px 16px', 
                  borderRadius: isMobile ? 10 : 12, 
                  border: '2px solid #e2e8f0', 
                  fontSize: isMobile ? 14 : 15, 
                  background: '#fff', 
                  color: '#1e293b', 
                  fontWeight: 600, 
                  outline: 'none', 
                  transition: 'all 0.15s',
                  minHeight: isMobile ? 44 : 'auto'
                }}
                onFocus={e => {
                  e.currentTarget.style.borderColor = '#FF7426';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,116,38,0.1)';
                }}
                onBlur={e => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              {numStudentsError && (
                <div style={{ 
                  color: '#dc2626', 
                  fontWeight: 600, 
                  marginTop: 8, 
                  fontSize: 13,
                  background: '#fee2e2',
                  padding: '10px 14px',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  border: '1px solid #fca5a5'
                }}>
                  <FaExclamationTriangle size={16} />
                  <span>{numStudentsError}</span>
                </div>
              )}
              
              {/* Participant email inputs */}
              {numStudents && Number(numStudents) >= 2 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#64748b', marginBottom: 10 }}>
                    <FaEnvelope size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> {isArabic ? `أدخل بريد ${Number(numStudents) - 1} طالب إضافي` : `Enter ${Number(numStudents) - 1} participant email(s)`}
                  </div>
                  {participantEmails.map((email, idx) => (
                    <div key={idx} style={{ marginBottom: 10 }}>
                      <input
                        type="email"
                        placeholder={`${isArabic ? 'بريد الطالب' : 'Participant'} ${idx + 1} ${isArabic ? '' : 'email'}`}
                        value={email}
                        onChange={e => {
                          const updated = [...participantEmails];
                          updated[idx] = e.target.value;
                          setParticipantEmails(updated);
                          setParticipantEmailsError('');
                        }}
                        style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '2px solid #e2e8f0', fontSize: 14, background: '#fff', color: '#1e293b', fontWeight: 500, outline: 'none', transition: 'all 0.15s' }}
                        onFocus={e => {
                          e.currentTarget.style.borderColor = '#FF7426';
                          e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,116,38,0.1)';
                        }}
                        onBlur={e => {
                          e.currentTarget.style.borderColor = '#e2e8f0';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                  ))}
                  {participantEmailsError && (
                    <div style={{ 
                      color: '#dc2626', 
                      fontWeight: 600, 
                      marginTop: 8, 
                      fontSize: 13, 
                      background: '#fee2e2', 
                      padding: '12px 16px', 
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      border: '1px solid #fca5a5'
                    }}>
                      <span style={{ fontSize: 18 }}>⚠️</span>
                      <span>{participantEmailsError}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
              {/* Mobile Next Button - Go to Lesson Format */}
              {isMobile && (
                <button
                  onClick={() => setMobileDetailsStep(3)}
                  style={{
                    width: '100%',
                    marginTop: 16,
                    padding: '14px',
                    background: '#1e293b',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 12,
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {isArabic ? '← التالي: نوع الدرس' : 'Next: Lesson Format →'}
                </button>
              )}
            </>
          )}

          {/* Mobile Step 6: Lesson Format (Online/Face-to-face) */}
          {(!isMobile || mobileDetailsStep === 3) && (
            <>
              {/* Step Title for Mobile */}
              {isMobile && (
                <div style={{ textAlign: 'center', marginBottom: 8 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <FaGraduationCap size={18} />
                    {isArabic ? 'نوع الدرس' : 'Lesson Format'}
                  </h3>
                </div>
              )}
              <div style={{ width: '100%' }}>
                {!isMobile && (
                  <label style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                    <FaGraduationCap size={15} /> {isArabic ? 'نوع الدرس' : 'Lesson Format'} <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                )}

                {/* If teacher selected "both", show options */}
                {(!teacher?.lesson_format_preference || teacher.lesson_format_preference === 'both') && (
                  <div style={{ display: 'flex', gap: isMobile ? 8 : 12, width: '100%' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setLessonFormat('online');
                        setSelectedPlace('');
                        setCustomPlace('');
                      }}
                      style={{
                        flex: 1,
                        padding: isMobile ? '12px 14px' : '14px 18px',
                        border: `2px solid ${lessonFormat === 'online' ? '#1E293B' : '#e5e7eb'}`,
                        background: lessonFormat === 'online' ? '#e2e8f0' : '#fff',
                        borderRadius: isMobile ? 10 : 12,
                        fontWeight: 600,
                        fontSize: isMobile ? 14 : 15,
                        cursor: 'pointer',
                        transition: isMobile ? 'none' : 'all 0.2s',
                        color: lessonFormat === 'online' ? '#1E293B' : '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: isMobile ? 6 : 8,
                        minHeight: isMobile ? 44 : 'auto'
                      }}
                    >
                      <FaLaptop size={15} />
                      {isArabic ? 'عبر الإنترنت' : 'Online'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setLessonFormat('in-person')}
                      style={{
                        flex: 1,
                        padding: isMobile ? '12px 14px' : '14px 18px',
                        border: `2px solid ${lessonFormat === 'in-person' ? '#1E293B' : '#e5e7eb'}`,
                        background: lessonFormat === 'in-person' ? '#e2e8f0' : '#fff',
                        borderRadius: isMobile ? 10 : 12,
                        fontWeight: 600,
                        fontSize: isMobile ? 14 : 15,
                        cursor: 'pointer',
                        transition: isMobile ? 'none' : 'all 0.2s',
                        color: lessonFormat === 'in-person' ? '#1E293B' : '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: isMobile ? 6 : 8,
                        minHeight: isMobile ? 44 : 'auto'
                      }}
                    >
                      <FaSchool size={15} />
                      {isArabic ? 'وجهاً لوجه' : 'Face-to-Face'}
                    </button>
                  </div>
                )}

                {/* If teacher selected "online_only", show locked online */}
                {teacher?.lesson_format_preference === 'online_only' && (
                  <div style={{
                    padding: '14px 18px',
                    border: '2px solid #1E293B',
                    background: '#e2e8f0',
                    borderRadius: 12,
                    fontWeight: 600,
                    fontSize: 15,
                    color: '#1E293B',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8
                  }}>
                    <FaLaptop size={15} />
                    {isArabic ? 'عبر الإنترنت (محدد بواسطة المعلم)' : 'Online (Set by teacher)'}
                  </div>
                )}

                {/* If teacher selected "face_to_face_only", show locked face-to-face */}
                {teacher?.lesson_format_preference === 'face_to_face_only' && (
                  <div style={{
                    padding: '14px 18px',
                    border: '2px solid #fbbf24',
                    background: '#fef3c7',
                    borderRadius: 12,
                    fontWeight: 600,
                    fontSize: 15,
                    color: '#92400e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8
                  }}>
                    <FaSchool size={15} />
                    {isArabic ? 'وجهاً لوجه (محدد بواسطة المعلم)' : 'Face-to-Face (Set by teacher)'}
                  </div>
                )}
              </div>

              {/* Place Selection for Face-to-Face */}
              {lessonFormat === 'in-person' && (teacher?.lesson_format_preference === 'face_to_face_only' || teacher?.lesson_format_preference === 'both') && (
                <div style={{ width: '100%' }}>
                  <label style={{ fontWeight: 700, fontSize: isMobile ? 14 : 15, marginBottom: isMobile ? 6 : 8, display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                    <FaMapMarkerAlt size={15} /> {isArabic ? 'اختر المكان' : 'Choose Location'} <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  
                  {/* Teacher's Preferred Place Option */}
                  {teacher?.preferred_place && (
                    <div
                      style={{
                        width: '100%',
                        padding: '14px 18px',
                        marginBottom: 12,
                        border: `2px solid ${selectedPlace === 'teacher_preferred' ? '#1E293B' : '#e5e7eb'}`,
                        background: selectedPlace === 'teacher_preferred' ? '#e2e8f0' : '#fff',
                        borderRadius: 12,
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10
                      }}
                      onClick={() => {
                        setSelectedPlace('teacher_preferred');
                        setCustomPlace('');
                      }}
                    >
                      <span style={{ fontSize: 18 }}>🏠</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: selectedPlace === 'teacher_preferred' ? '#1E293B' : '#64748b' }}>
                          {isArabic ? 'المكان المفضل للمعلم' : 'Teacher\'s Preferred Place'}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                          {teacher.preferred_place}
                        </div>
                        <a
                          href={createMapsSearchUrl(teacher.preferred_place)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            color: '#2563eb',
                            textDecoration: 'none',
                            fontWeight: 600,
                            fontSize: 12,
                            marginTop: 4,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                        >
                          <FaMapMarkerAlt size={14} style={{ display: 'inline', marginRight: 4 }} /> {isArabic ? 'عرض على الخريطة' : 'View on Map'} ↗
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {/* Student's Preferred Location Option */}
                  {studentPreferredLocation?.formatted_address && (
                    <div
                      style={{
                        width: '100%',
                        padding: '14px 18px',
                        marginBottom: 12,
                        border: `2px solid ${selectedPlace === 'student_preferred' ? '#1E293B' : '#e5e7eb'}`,
                        background: selectedPlace === 'student_preferred' ? '#e2e8f0' : '#fff',
                        borderRadius: 12,
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10
                      }}
                      onClick={() => {
                        setSelectedPlace('student_preferred');
                        setCustomPlace('');
                        // Set location data from student's saved location
                        setLocationData({
                          latitude: studentPreferredLocation.latitude,
                          longitude: studentPreferredLocation.longitude,
                          place_id: studentPreferredLocation.place_id,
                          formatted_address: studentPreferredLocation.formatted_address
                        });
                      }}
                    >
                      <span style={{ fontSize: 18 }}>🏡</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: selectedPlace === 'student_preferred' ? '#1E293B' : '#64748b' }}>
                          {studentPreferredLocation.name || (isArabic ? 'موقعي المفضل' : 'My Preferred Location')}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                          {studentPreferredLocation.formatted_address}
                        </div>
                        <a
                          href={studentPreferredLocation.latitude && studentPreferredLocation.longitude
                            ? createMapsUrl(studentPreferredLocation.latitude, studentPreferredLocation.longitude)
                            : createMapsSearchUrl(studentPreferredLocation.formatted_address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            color: '#2563eb',
                            textDecoration: 'none',
                            fontWeight: 600,
                            fontSize: 12,
                            marginTop: 4,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                        >
                          <FaMapMarkerAlt size={14} style={{ display: 'inline', marginRight: 4 }} /> {isArabic ? 'عرض على الخريطة' : 'View on Map'} ↗
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {/* Custom Place Option */}
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPlace('custom');
                      if (!customPlace) setCustomPlace('');
                    }}
                    style={{
                      width: '100%',
                      padding: '14px 18px',
                      marginBottom: 12,
                      border: `2px solid ${selectedPlace === 'custom' ? '#1E293B' : '#e5e7eb'}`,
                      background: selectedPlace === 'custom' ? '#e2e8f0' : '#fff',
                      borderRadius: 12,
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10
                    }}
                  >
                    <FaMapMarkerAlt size={18} />
                    <div style={{ fontWeight: 700, color: selectedPlace === 'custom' ? '#1E293B' : '#64748b' }}>
                      {isArabic ? 'مكان آخر' : 'Other Location'}
                    </div>
                  </button>
                  
                  {/* Custom Place Input with Map */}
                  {selectedPlace === 'custom' && (
                    <div style={{ marginTop: 12 }}>
                      <LocationPicker
                        initialLocation={locationData ? { lat: locationData.latitude, lng: locationData.longitude } : null}
                        onLocationSelect={(location) => {
                          if (location) {
                            setLocationData(location);
                            setCustomPlace(location.formatted_address || location.place_name || '');
                          } else {
                            setLocationData(null);
                            setCustomPlace('');
                          }
                        }}
                        mode="single"
                        showAutocomplete={true}
                        showClickToSelect={true}
                        readOnly={false}
                        height={isMobile ? '350px' : '400px'}
                        isArabic={isArabic}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* File Upload */}
              <div style={{ width: '100%' }}>
            <label style={{ fontWeight: 700, fontSize: isMobile ? 14 : 15, marginBottom: isMobile ? 6 : 8, display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
              <FaPaperclip size={15} /> {t.uploadMaterials}
            </label>
            <label htmlFor="study-files-upload" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: isMobile ? 6 : 8, 
              background: '#f1f5f9', 
              border: '2px dashed #cbd5e1', 
              color: '#475569', 
              padding: isMobile ? '14px 16px' : '16px 20px', 
              borderRadius: isMobile ? 10 : 12, 
              cursor: 'pointer', 
              fontWeight: 600, 
              fontSize: isMobile ? 13 : 14, 
              transition: isMobile ? 'none' : 'all 0.2s',
              minHeight: isMobile ? 44 : 'auto'
            }}
              onMouseOver={e => {
                if (!isMobile) {
                  e.currentTarget.style.background = '#e2e8f0';
                  e.currentTarget.style.borderColor = '#1E293B';
                  e.currentTarget.style.color = '#1E293B';
                }
              }}
              onMouseOut={e => {
                if (!isMobile) {
                  e.currentTarget.style.background = '#f1f5f9';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                  e.currentTarget.style.color = '#475569';
                }
              }}
            >
              <span style={{ fontSize: isMobile ? 16 : 18 }}>📁</span> {t.chooseFiles}
            </label>
            <input id="study-files-upload" type="file" multiple onChange={handleFileChange} style={{ display: 'none' }} />
            {studyFiles.length > 0 && (
              <ul style={{ margin: '12px 0 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {studyFiles.map((file, idx) => (
                  <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#e2e8f0', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#1E293B' }}>
                    <FaFile size={14} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(idx)}
                      style={{
                        background: 'rgba(220,38,38,0.1)',
                        border: 'none',
                        color: '#dc2626',
                        fontWeight: 700,
                        fontSize: 16,
                        cursor: 'pointer',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.15s'
                      }}
                      onMouseOver={e => e.currentTarget.style.background = '#fca5a5'}
                      onMouseOut={e => e.currentTarget.style.background = 'rgba(220,38,38,0.1)'}
                      aria-label={`Remove ${file.name}`}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
              </div>

              {/* Coupon Selector */}
              {availableCoupons.length > 0 && (
                <div style={{ width: '100%' }}>
              <label style={{ fontWeight: 700, fontSize: isMobile ? 14 : 15, marginBottom: isMobile ? 6 : 8, display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}>
                <FaTicketAlt size={15} /> {t.applyCoupon}
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 8 : 10 }}>
                <select
                  value={selectedCoupon?.id || ''}
                  onChange={(e) => {
                    const couponId = e.target.value;
                    if (couponId) {
                      const coupon = availableCoupons.find(c => c.id === couponId);
                      setSelectedCoupon(coupon || null);
                    } else {
                      setSelectedCoupon(null);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: isMobile ? '12px 36px 12px 14px' : '14px 18px',
                    border: '2px solid #cbd5e1',
                    borderRadius: isMobile ? 10 : 12,
                    fontSize: isMobile ? 14 : 15,
                    fontWeight: 600,
                    background: '#fff',
                    color: '#1E293B',
                    cursor: 'pointer',
                    transition: isMobile ? 'none' : 'all 0.2s',
                    minHeight: isMobile ? 44 : 'auto'
                  }}
                >
                  <option value="">{t.selectCoupon}</option>
                  {availableCoupons.map(coupon => (
                    <option key={coupon.id} value={coupon.id}>
                      {coupon.label} - {isArabic ? toArabicNumber(coupon.discount_percentage) : coupon.discount_percentage}% {isArabic ? 'خصم' : 'OFF'}
                    </option>
                  ))}
                </select>

                {selectedCoupon && (
                  <div style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: 12,
                    padding: 16,
                    color: '#fff'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 700 }}>{t.couponDiscount}:</span>
                      <button
                        type="button"
                        onClick={() => setSelectedCoupon(null)}
                        style={{
                          background: 'rgba(255,255,255,0.2)',
                          border: 'none',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: 'pointer',
                          padding: '6px 12px',
                          borderRadius: 6,
                          transition: 'all 0.15s'
                        }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                        onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                      >
                        {t.removeCoupon}
                      </button>
                    </div>
                    <div style={{ fontSize: 13, opacity: 0.95, marginBottom: 4 }}>
                      {selectedCoupon.label}
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 900 }}>
                      -{isArabic ? toArabicNumber(selectedCoupon.discount_percentage) : selectedCoupon.discount_percentage}%
                    </div>
                  </div>
                )}
                </div>
              </div>
              )}

              {/* Price Summary - Only show when selectedDate and timeRange are set */}
              {(() => {
            if (!selectedDate || !timeRange.from || !timeRange.to) return null;

            const duration = getDurationInHours(timeRange.from, timeRange.to);
            let calculatedOriginalPrice = 0;
            if (requestType === 'group') {
              // Check teacher's group pricing model (fixed vs per_person)
              const groupPricingModel = teacher.group_pricing_model || 'per_person';
              if (groupPricingModel === 'fixed') {
                // Fixed price for entire group
                calculatedOriginalPrice = (Number(teacher.group_hourly_rate) || 0) * duration;
              } else {
                // Per-person pricing (default)
                calculatedOriginalPrice = (Number(teacher.group_hourly_rate) || 0) * duration * (Number(numStudents) || 1);
              }
            } else {
              calculatedOriginalPrice = (Number(teacher.hourly_rate) || 0) * duration;
            }

            let calculatedFinalPrice = calculatedOriginalPrice;
            let calculatedDiscount = 0;
            if (selectedCoupon) {
              calculatedDiscount = (calculatedOriginalPrice * selectedCoupon.discount_percentage) / 100;
              calculatedFinalPrice = Math.max(0, calculatedOriginalPrice - calculatedDiscount);
            }

            return (
              <div style={{
                width: '100%',
                background: selectedCoupon ? 'linear-gradient(135deg, #FF7426 0%, #FF8C42 100%)' : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                borderRadius: 12,
                padding: 16,
                color: selectedCoupon ? '#fff' : '#1e293b',
                border: selectedCoupon ? 'none' : '2px solid #cbd5e1'
              }}>
                {selectedCoupon && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14, opacity: 0.9 }}>
                      <span>{t.originalPrice}:</span>
                      <span style={{ textDecoration: 'line-through' }}>{isArabic ? toArabicNumber(calculatedOriginalPrice.toFixed(3)) : calculatedOriginalPrice.toFixed(3)} BD</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14, fontWeight: 700, color: '#fef3c7' }}>
                      <span>{t.couponDiscount}:</span>
                      <span>-{isArabic ? toArabicNumber(calculatedDiscount.toFixed(3)) : calculatedDiscount.toFixed(3)} BD</span>
                    </div>
                  </>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 900 }}>
                  <span>{t.finalPrice}:</span>
                  <span>{isArabic ? toArabicNumber(calculatedFinalPrice.toFixed(3)) : calculatedFinalPrice.toFixed(3)} BD</span>
                </div>
              </div>
              );
              })()}

              {/* Book Button */}
              <button
                onClick={handleBook}
                disabled={
                  !selectedDate || 
                  !timeRange.from || 
                  !timeRange.to || 
                  !selectedSubject || 
                  submitting ||
                  (lessonFormat === 'in-person' && !selectedPlace) ||
                  (lessonFormat === 'in-person' && selectedPlace === 'custom' && !customPlace.trim())
                }
            style={{
              background: '#1E293B',
              color: '#fff',
              fontWeight: 800,
              fontSize: isMobile ? 16 : 18,
              border: 'none',
              borderRadius: isMobile ? 12 : 14,
              padding: isMobile ? '16px 24px' : '18px 32px',
              width: '100%',
              marginTop: isMobile ? 6 : 8,
              boxShadow: '0 8px 24px rgba(30,41,59,0.3)',
              cursor: submitting ? 'wait' : 'pointer',
              transition: isMobile ? 'none' : 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? 8 : 10,
              minHeight: isMobile ? 50 : 'auto',
              opacity: (
                !selectedDate || 
                !timeRange.from || 
                !timeRange.to || 
                !selectedSubject || 
                submitting ||
                (lessonFormat === 'in-person' && !selectedPlace) ||
                (lessonFormat === 'in-person' && selectedPlace === 'custom' && !customPlace.trim())
              ) ? 0.6 : 1
            }}
            onMouseOver={e => {
              if (!isMobile && !submitting && selectedDate && timeRange.from && timeRange.to && selectedSubject && 
                  !(lessonFormat === 'in-person' && !selectedPlace) &&
                  !(lessonFormat === 'in-person' && selectedPlace === 'custom' && !customPlace.trim())) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(30,41,59,0.4)';
              }
            }}
            onMouseOut={e => {
              if (!isMobile) {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(30,41,59,0.3)';
              }
            }}
          >
            <FaRocket size={isMobile ? 18 : 20} style={{ marginRight: 4, verticalAlign: 'middle' }} /> {t.requestSession}
          </button>
            </>
          )}
        </div>
            )}
          </>
      )}
      {/* Hide these sections when modal is open (when there are daySessions to display) */}
      {selectedDateRequest && !selectedDate && (
        <div style={{ maxWidth: 600, margin: '24px auto 0', background: '#fff7ed', borderRadius: 14, padding: 20, border: '1px solid #ffddb0' }}>
          <div style={{ fontWeight: 800, color: '#b45309', marginBottom: 8 }}>
            {selectedDateRequest.reschedule_status === 'pending'
              ? (isArabic ? 'طلب إعادة الجدولة قيد الانتظار' : 'Reschedule request pending')
              : selectedDateRequest.reschedule_status === 'declined'
                ? (isArabic ? 'تم رفض إعادة الجدولة السابقة' : 'Previous reschedule declined')
                : (isArabic ? 'يمكنك إعادة جدولة هذه الجلسة' : 'You can reschedule this session')}
          </div>
          <div style={{ fontSize: 14, color: '#7c2d12', marginBottom: 12 }}>
            {(isArabic ? 'الوقت المؤكد الحالي هو ' : 'Current confirmed time: ') + `${selectedDateRequest.original_date || selectedDateRequest.date} • ${selectedDateRequest.original_time_from || selectedDateRequest.time_from} – ${selectedDateRequest.original_time_to || selectedDateRequest.time_to}`}
            {selectedDateRequest.reschedule_status === 'pending' && selectedDateRequest.reschedule_requested_date && (
              <><br />{(isArabic ? 'الوقت المطلوب: ' : 'Requested time: ') + `${selectedDateRequest.reschedule_requested_date} • ${selectedDateRequest.reschedule_requested_time_from} – ${selectedDateRequest.reschedule_requested_time_to}`}</>
            )}
          </div>
          {selectedDateRequest.reschedule_status !== 'pending' && (
            <button
              onClick={() => openRescheduleModal(selectedDateRequest)}
              style={{ background: '#1E293B', color: '#fff', padding: '10px 18px', borderRadius: 10, border: 'none', fontWeight: 700, cursor: 'pointer' }}
            >
              {isArabic ? 'إعادة جدولة الجلسة' : 'Request Reschedule'}
            </button>
          )}
        </div>
      )}
      {selectedDateRequest && !selectedDate && (
        <div style={{ maxWidth: 600, margin: '18px auto 0', background: '#ffffff', borderRadius: 16, boxShadow: '0 8px 32px rgba(149,157,165,0.15)', padding: 22, border: '1px solid #e5ebff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: '#232B3E' }}>{isTeacherRoute ? selectedDateRequest.student_name : selectedDateRequest.teacher_name}</div>
            <span style={{
              background: selectedDateRequest.status === 'accepted' ? '#dcfce7' : selectedDateRequest.status === 'pending' ? '#fef3c7' : '#fee2e2',
              color: selectedDateRequest.status === 'accepted' ? '#166534' : selectedDateRequest.status === 'pending' ? '#92400e' : '#b91c1c',
              padding: '4px 10px',
              borderRadius: 999,
              fontWeight: 700,
              fontSize: 12
            }}>
              {isArabic
                ? (selectedDateRequest.status === 'accepted' ? 'مقبول' : selectedDateRequest.status === 'pending' ? 'قيد الانتظار' : 'مرفوض')
                : selectedDateRequest.status}
            </span>
          </div>
          <div style={{ display: 'grid', gap: 10, fontSize: 14, color: '#4b5563' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 600 }}>{isArabic ? 'التاريخ:' : 'Date:'}</span>
              <span>{toYMD(new Date(selectedDateRequest.date)) === selectedDateRequest.date ? format(new Date(selectedDateRequest.date), 'PPP') : selectedDateRequest.date}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 600 }}>{isArabic ? 'الوقت:' : 'Time:'}</span>
              <span>{isArabic ? `${toArabicTime(selectedDateRequest.time_from || '--')} — ${toArabicTime(selectedDateRequest.time_to || '--')}` : `${selectedDateRequest.time_from || '--'} — ${selectedDateRequest.time_to || '--'}`}</span>
            </div>
            {selectedDateRequest.total_price && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 600 }}>{isArabic ? 'السعر الكلي:' : 'Total price:'}</span>
                <span>{isArabic ? toArabicNumber(selectedDateRequest.total_price) : selectedDateRequest.total_price} BD</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 600 }}>{isArabic ? 'نوع الطلب:' : 'Request type:'}</span>
              <span style={{ textTransform: 'capitalize' }}>{selectedDateRequest.request_type || '--'}</span>
            </div>
            {selectedDateRequest.reschedule_status === 'pending' && (
              <div style={{ background: '#fff4e6', border: '1px dashed #fbbf24', borderRadius: 10, padding: 12 }}>
                <div style={{ fontWeight: 700, color: '#b45309', marginBottom: 4 }}>{isArabic ? 'بانتظار الموافقة' : 'Awaiting approval'}</div>
                <div style={{ color: '#9a3412' }}>
                  {selectedDateRequest.reschedule_reason
                    ? selectedDateRequest.reschedule_reason
                    : (isArabic ? 'تم إرسال طلب إعادة الجدولة إلى المعلم.' : 'Your reschedule request has been sent to the teacher.')}
                </div>
              </div>
            )}
            {selectedDateRequest.meeting_link && selectedDateRequest.status === 'accepted' && (
              <div style={{ background: '#e2e8f0', borderRadius: 10, padding: 12, color: '#1E293B' }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>{isArabic ? 'رابط الجلسة' : 'Session link'}</div>
                <a href={selectedDateRequest.meeting_link} target="_blank" rel="noreferrer" style={{ color: '#1E293B', fontWeight: 600 }}>
                  {selectedDateRequest.meeting_link}
                </a>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Removed old "booking disabled" message since we now allow booking on days with existing sessions but show conflict warnings */}
      {rescheduleState.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <form onSubmit={submitRescheduleRequest} style={{ background: '#fff', borderRadius: 16, padding: 24, width: '90%', maxWidth: 420, boxShadow: '0 12px 40px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', gap: 14, overflow: 'visible' }}>
            <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#232B3E' }}>{isArabic ? 'إعادة جدولة الجلسة' : 'Reschedule Session'}</h3>
            <label style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{isArabic ? 'التاريخ الجديد' : 'New date'}</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal h-12"
                  style={{ borderRadius: 10, border: '1.5px solid #e0eaff', fontWeight: 600, background: '#f5f8fe' }}
                >
                  <span style={{ flex: 1 }}>
                    {rescheduleState.date ? format(new Date(rescheduleState.date), 'PPP') : (isArabic ? 'اختر تاريخًا' : 'Pick a date')}
                  </span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start" style={{ zIndex: 9999, borderRadius: 16 }}>
                <ShadcnCalendar
                  mode="single"
                  selected={rescheduleState.date ? new Date(rescheduleState.date) : undefined}
                  onSelect={(date) => {
                    if (date) {
                      setRescheduleState(prev => ({ ...prev, date: format(date, 'yyyy-MM-dd') }));
                    }
                  }}
                  disabled={(date) => {
                    const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
                    return isPast;
                  }}
                  initialFocus
                  className="rounded-2xl"
                />
              </PopoverContent>
            </Popover>
            <label style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{isArabic ? 'الوقت الجديد' : 'New time'}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <TimeSelect
                  value={rescheduleState.from}
                  onChange={val => setRescheduleState(prev => ({ ...prev, from: val }))}
                  placeholder={t.fromPlaceholder}
                  isArabic={isArabic}
                />
              </div>
              <div style={{ flex: 1 }}>
                <TimeSelect
                  value={rescheduleState.to}
                  onChange={val => setRescheduleState(prev => ({ ...prev, to: val }))}
                  placeholder={t.toPlaceholder}
                  isArabic={isArabic}
                />
              </div>
            </div>
            <label style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{isArabic ? 'سبب (اختياري)' : 'Reason (optional)'}</label>
            <textarea
              value={rescheduleState.reason}
              onChange={e => setRescheduleState(prev => ({ ...prev, reason: e.target.value }))}
              rows={3}
              style={{ padding: 10, borderRadius: 10, border: '1.5px solid #e0eaff', resize: 'vertical' }}
            />
            {rescheduleState.error && <div style={{ background: '#ffe2e2', color: '#c62828', padding: 10, borderRadius: 10, fontWeight: 600 }}>{rescheduleState.error}</div>}
            {rescheduleState.success && <div style={{ background: '#bbf7d0', color: '#166534', padding: 10, borderRadius: 10, fontWeight: 600 }}>{rescheduleState.success}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
              <button type="button" onClick={closeRescheduleModal} style={{ flex: 1, background: '#e5e7eb', color: '#111827', padding: '10px 16px', borderRadius: 10, border: 'none', fontWeight: 700, cursor: 'pointer' }}>{isArabic ? 'إلغاء' : 'Cancel'}</button>
              <button type="submit" disabled={rescheduleState.loading} style={{ flex: 1, background: '#1E293B', color: '#fff', padding: '10px 16px', borderRadius: 10, border: 'none', fontWeight: 700, cursor: 'pointer' }}>
                {rescheduleState.loading ? (isArabic ? 'جارٍ الإرسال...' : 'Sending...') : (isArabic ? 'إرسال الطلب' : 'Send request')}
              </button>
            </div>
          </form>
        </div>
      )}
          </div>
        </div>
      )}
    </main>
  );
} 